import {
	type Abrechnung,
	type AenderungDto,
	type Ampel,
	type AngebotArchivZeile,
	type AngebotDto,
	type AngebotInhalt,
	type AngebotKundenSicht,
	type AngebotOeffentlich,
	type AngebotsVorlageDto,
	type BausteinDaten,
	type BausteinDto,
	type BausteinKategorie,
	type BausteinRolle,
	type Betreuung,
	type BetreuungPhase,
	type BetreuungsStufe,
	type BrandAssetDto,
	type Customer,
	createApiClient,
	type DistanzErgebnis,
	type Drehtag,
	type DrehtagTyp,
	type DrehtagTypRegel,
	type EinstellungEingabe,
	type EinstellungErgebnis,
	type EngineErgebnis,
	type FinanzenUebersicht,
	type FinanzVorschlagArt,
	type FinanzVorschlagStatus,
	type FixkostenDaten,
	type FixkostenDto,
	type GespraechDto,
	type HealthStatus,
	type InteressentAkte,
	type InteressentDto,
	type InteressentZeile,
	type KalkBausteinDto,
	type KalkParameter,
	type KalkulationDaten,
	type KalkulationDto,
	type KalkulationStatus,
	type KalkulationZeile,
	type VorlageDaten as KalkVorlageDaten,
	type VorlageDto as KalkVorlageDto,
	type Katalog,
	type KontingentStand,
	type KonzeptAntwort,
	type KonzeptAuftrag,
	type KostensatzErgebnis,
	type Kundentyp,
	type KundenVerlauf,
	type Land,
	type MonatDaten,
	type MonatDto,
	type NummernStand,
	type PaketZiel,
	type PersonDaten,
	type PersonDto,
	type PersonRolle,
	type Position,
	type PositionTyp,
	type PruefStufe,
	type Pruefung,
	type ReiseErgebnis,
	type ReminderDto,
	type Rhythmus,
	type Stammdaten,
	type Stufe,
	type StufenErgebnis,
	type Szenario,
	type TestimonialPartnerDto,
	type TestimonialStatus,
	type TestimonialUebersicht,
	type VorschlagDto,
	type ZaehltAls,
	type Zahlungsbedingung,
	type Zone,
} from "@olgen/api";
import type { TeamRolle } from "@olgen/api/auth";
import { queryOptions } from "@tanstack/react-query";
import { vorschauHeader } from "@/lib/kundenVorschau";
import type { Dienstleistung } from "@/lib/types";

export type { BrandAssetDto, Customer, KonzeptAntwort, KonzeptAuftrag };

/**
 * Einziges Modul im Frontend, das über Eden mit dem Backend spricht. In den
 * React-Komponenten steht keine Request-Logik. Die Anmeldung liegt daneben in
 * `auth.ts`, weil sie nicht über Eden läuft.
 *
 * Die baseUrl ist immer der eigene Origin: In der Entwicklung leitet Vite
 * `/api` an das Backend weiter, im Container liefert das Backend beides aus.
 * Nur so schickt der Browser den Sitzungs-Cookie überhaupt mit.
 */
const client = createApiClient({ baseUrl: window.location.origin, headers: vorschauHeader });

export async function fetchStatus(): Promise<HealthStatus> {
	const { data, error } = await client.api.status.get();

	if (error !== null) {
		// Die Meldung landet sichtbar auf der Seite. Sie muss deshalb auch für
		// jemanden brauchbar sein, der keine Entwicklertools öffnet.
		throw new Error(
			`GET ${window.location.origin}/api/status hat mit Status ${String(error.status)} geantwortet.`,
		);
	}

	return data;
}

export const statusQuery = queryOptions({
	queryKey: ["status"],
	queryFn: fetchStatus,
});

/**
 * Schickt eine Fehlermeldung an das Backend. Der Aufrufer ist
 * `reportError` und niemand sonst.
 *
 * Der Parameter ist absichtlich strukturell getippt statt aus `reportError`
 * importiert — sonst importierten sich die beiden Module gegenseitig.
 *
 * Der Endpunkt verlangt seit dem Login eine Sitzung. Ein Fehler auf der
 * Anmeldeseite kommt hier also nicht mehr durch; er bleibt in der
 * Browser-Konsole. `reportError` verschluckt das Scheitern bewusst.
 */
export async function sendErrorReport(report: {
	message: string;
	stack: string | null;
	origin: string;
	url: string;
	userAgent: string;
	timestamp: string;
}): Promise<void> {
	await client.api.errors.post(report);
}

// ─── Kunden ─────────────────────────────────────────────────────────────────

/**
 * Was das Formular liefert: überall Strings, leere Felder sind "". Die
 * Übersetzung in `null` für die API passiert hier und nicht in der
 * Komponente — die Oberfläche soll von der Drahtform nichts wissen.
 */
/** Ein weiterer Ansprechpartner im Formular — überall Strings, wie der Rest. */
export type KontaktFormularDaten = {
	name: string;
	email: string;
	telefon: string;
	/** Bekommt automatische Mails; abschalten statt löschen, wenn jemand keine Post mehr will. */
	mailsAktiv: boolean;
};

export type CustomerFormularDaten = {
	name: string;
	/** Leer = automatisch vergeben; sonst die Zahl, die in der Kartei steht. */
	kundennummer: string;
	industry: string;
	contactName: string;
	contactEmail: string;
	contactPhone: string;
	/** Anschrift „Straße\nPLZ Ort" (15.09.2026) — leer = keine. */
	adresse: string;
	/** Weitere Ansprechpartner (04.09.2026) — der Hauptkontakt steht in den contact*-Feldern. */
	kontakte: KontaktFormularDaten[];
	notes: string;
	/** Was der Kunde gekauft hat — ein Kunde wird erst nach dem Kauf angelegt. */
	purchased: string;
	/** Partneragentur (White Label) — meldet Aufträge und sieht ihre Endkunden. */
	istAgentur: boolean;
	/** White-Label-Kunde: nie automatische E-Mails von uns — die Agentur kommuniziert. */
	whiteLabel: boolean;
};

function zuApiFeld(wert: string): string | null {
	const getrimmt = wert.trim();

	return getrimmt === "" ? null : getrimmt;
}

function zuApiBody(daten: CustomerFormularDaten) {
	const nummer = Number.parseInt(daten.kundennummer.trim(), 10);
	return {
		name: daten.name.trim(),
		// Leer heißt „nicht anfassen" (beim Anlegen: automatisch vergeben).
		...(Number.isNaN(nummer) ? {} : { kundennummer: nummer }),
		industry: zuApiFeld(daten.industry),
		contactName: zuApiFeld(daten.contactName),
		contactEmail: zuApiFeld(daten.contactEmail),
		contactPhone: zuApiFeld(daten.contactPhone),
		adresse: zuApiFeld(daten.adresse),
		// Leere Zeilen (weder Name noch Adresse) fliegen raus, halb gefüllte
		// meldet der Server mit einem verständlichen Satz.
		kontakte: daten.kontakte
			.filter((k) => k.name.trim() !== "" || k.email.trim() !== "")
			.map((k) => ({
				name: k.name.trim(),
				email: k.email.trim(),
				telefon: zuApiFeld(k.telefon),
				mailsAktiv: k.mailsAktiv,
			})),
		notes: zuApiFeld(daten.notes),
		purchased: zuApiFeld(daten.purchased),
		istAgentur: daten.istAgentur,
		whiteLabel: daten.whiteLabel,
	};
}

/**
 * Zieht den Satz aus dem Fehler-Body, den das Backend für Menschen
 * mitschickt (409 „Name existiert bereits", 404 „gibt es nicht mehr"). Wo
 * keiner steht — etwa bei einem Netzwerkabriss — bleibt der Rückfalltext.
 */
function fehlermeldungVon(args: {
	value: object | string | number | null;
	fallback: string;
}): string {
	if (
		typeof args.value === "object" &&
		args.value !== null &&
		"error" in args.value &&
		typeof args.value.error === "string"
	) {
		return args.value.error;
	}

	return args.fallback;
}

export async function fetchCustomers(): Promise<Customer[]> {
	const { data, error } = await client.api.customers.get();

	if (error !== null) {
		throw new Error(
			`Die Kundenliste konnte nicht geladen werden (Status ${String(error.status)}).`,
		);
	}

	return data;
}

export const customersQuery = queryOptions({
	queryKey: ["customers"],
	queryFn: fetchCustomers,
});

/** Ein Konto mit Zugang zum Kunden-Portal — `letzterLogin` null heißt „Nie angemeldet". */
export type KundenZugangDto = {
	userId: string;
	name: string;
	email: string;
	letzterLogin: string | null;
	/** Gesperrt (ausgeschiedene Person) — die Anmeldung ist zu, das Konto bleibt. */
	gesperrtAm: string | null;
};

/**
 * Das Kartei-Detail (T020/T021): Stammdaten plus Zugänge samt letzter
 * Anmeldung — gespeist aus der Session-Tabelle, kein gepflegtes Feld.
 */
export const kundenDetailQuery = (id: string) =>
	queryOptions({
		queryKey: ["customers", id],
		queryFn: async (): Promise<Customer & { zugaenge: KundenZugangDto[] }> => {
			const { data, error } = await client.api.customers({ id }).get();

			if (error !== null) {
				throw new Error(
					fehlermeldungVon({
						value: error.value,
						fallback: "Das Kunden-Detail konnte nicht geladen werden.",
					}),
				);
			}

			return data;
		},
	});

export async function createCustomer(daten: CustomerFormularDaten): Promise<Customer> {
	const { data, error } = await client.api.customers.post(zuApiBody(daten));

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({ value: error.value, fallback: "Der Kunde konnte nicht angelegt werden." }),
		);
	}

	return data;
}

export async function updateCustomer(args: {
	id: string;
	daten: CustomerFormularDaten;
}): Promise<Customer> {
	const { data, error } = await client.api.customers({ id: args.id }).patch(zuApiBody(args.daten));

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Die Änderungen konnten nicht gespeichert werden.",
			}),
		);
	}

	return data;
}

/**
 * Die Kundenkartei: archivierte Kunden. „Löschen" gibt es nicht — ein Kunde
 * wandert mit allen Daten (Onboardings, Konzepte, Marken-Unterlagen,
 * Einwilligungen) hierher und kommt mit einem Klick zurück.
 */
export const kundenArchivQuery = queryOptions({
	queryKey: ["customers-archiv"],
	queryFn: async (): Promise<Customer[]> => {
		const { data, error } = await client.api.customers.archiv.get();
		if (error !== null) {
			throw new Error(
				`Die Kundenkartei konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function archiviereKunde(args: { id: string }): Promise<Customer> {
	const { data, error } = await client.api.customers({ id: args.id }).archivieren.post();

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Kunde konnte nicht archiviert werden.",
			}),
		);
	}

	return data;
}

/**
 * Das unwiderrufliche Löschen aus der Kundenkartei — nimmt ALLES mit:
 * Onboardings, Konzepte, Kommentare, Einwilligungen, Marken-Unterlagen.
 * Das Backend verweigert es für Kunden, die nicht archiviert sind.
 */
export async function loescheKundeEndgueltig(args: { id: string }): Promise<void> {
	const { error } = await client.api.customers({ id: args.id }).delete();

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Kunde konnte nicht gelöscht werden.",
			}),
		);
	}
}

export async function stelleKundeWiederHer(args: { id: string }): Promise<Customer> {
	const { data, error } = await client.api.customers({ id: args.id }).wiederherstellen.post();

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Kunde konnte nicht wiederhergestellt werden.",
			}),
		);
	}

	return data;
}

// ─── Onboarding-Verlauf ─────────────────────────────────────────────────────

/** Das KI-Ergebnis ohne die Quelle-Angabe — so wird es am Verlauf gespeichert. */
export type KonzeptInhalt = Omit<KonzeptAntwort, "quelle">;

/**
 * Der gespeicherte Stand eines Onboarding-Gesprächs — abgehakte Punkte,
 * Notizen, Transkript, das fertige Konzept und der Teilen-Token der
 * Drehvorbereitung. Damit lässt sich ein unterbrochener Call später (und
 * auf jedem Gerät) genau dort fortsetzen.
 */
export type OnboardingVerlaufDto = {
	id: string;
	name: string;
	kundenart: "direktkunde" | "agentur";
	dienstleistungen: Dienstleistung[];
	tiefe: "kompakt" | "ausfuehrlich";
	gekauft: string | null;
	transkript: string | null;
	besprochen: string[];
	notiert: Record<string, string>;
	/** Was der Kunde vorab selbst beantwortet hat (Frage-Id → Antwort). */
	kundenAntworten: Record<string, string>;
	kundenAntwortenAm: string | null;
	konzept: KonzeptInhalt | null;
	konzeptErstellt: boolean;
	/** Welche Drehbuch-Bausteine das Projekt braucht — reine Interview-Drehs lassen den Rest weg. */
	mitHooks: boolean;
	mitCta: boolean;
	mitReels: boolean;
	/** Der Änderungsverlauf der Drehvorbereitung — neuester Eintrag zuerst. */
	anpassungen: { datum: string; text: string }[];
	/** Der Kunde dieses Onboardings — `null` nur bei Verläufen aus der Zeit davor. */
	customerId: string | null;
	/** Zugang zur geteilten Drehvorbereitung — vergibt der Server beim ersten Speichern. */
	teilenToken: string | null;
	/** Freigabekette (T085): erst „veroeffentlicht" zeigt der Teilen-Link etwas. */
	veroeffentlichung: "entwurf" | "geplant" | "veroeffentlicht";
	/** ISO-Zeitpunkt der geplanten Veröffentlichung, sonst null. */
	veroeffentlichtAb: string | null;
	/** Die Kundenfreigabe (16.09.2026) — eine inhaltliche Änderung setzt sie zurück. */
	kundenFreigabe: KundenFreigabeDto | null;
	updatedAt: string;
};

export async function fetchOnboardingVerlaeufe(): Promise<OnboardingVerlaufDto[]> {
	const { data, error } = await client.api.onboarding.get();

	if (error !== null) {
		throw new Error(
			`Die Onboarding-Verläufe konnten nicht geladen werden (Status ${String(error.status)}).`,
		);
	}

	return data;
}

export const onboardingVerlaeufeQuery = queryOptions({
	queryKey: ["onboarding-verlaeufe"],
	queryFn: fetchOnboardingVerlaeufe,
});

/**
 * Speichert den kompletten Stand einer Journey (Upsert) — Write-Behind nach
 * jeder Änderung. Die Antwort trägt den (ggf. frisch vergebenen)
 * Teilen-Token der Drehvorbereitung.
 */
export async function speichereOnboardingVerlauf(args: {
	id: string;
	daten: Omit<
		OnboardingVerlaufDto,
		| "id"
		| "updatedAt"
		| "teilenToken"
		| "anpassungen"
		| "customerId"
		| "veroeffentlichung"
		| "veroeffentlichtAb"
		| "kundenFreigabe"
		// Die Kunden-Antworten gehören dem Kunden — das Team schickt sie nie mit.
		| "kundenAntworten"
		| "kundenAntwortenAm"
	> & {
		/** Hängt einen Eintrag an den Änderungsverlauf an (Datum setzt der Server). */
		neueAnpassung?: string;
		/** Weglassen heißt: die Kundenverbindung nicht anfassen. */
		customerId?: string;
	};
}): Promise<OnboardingVerlaufDto> {
	const { data, error } = await client.api.onboarding({ id: args.id }).put(args.daten);

	if (error !== null) {
		throw new Error(
			`Der Gesprächsstand konnte nicht gespeichert werden (Status ${String(error.status)}).`,
		);
	}

	return data;
}

/**
 * Lässt die KI die Unterlagen der Agentur-Anfrage lesen und den Leitfaden
 * vorbefüllen. Der Fragenkatalog reist mit, weil er im Frontend lebt
 * (`lib/onboarding.ts`) und an den gewählten Dienstleistungen hängt.
 */
/**
 * Web-Recherche (04.09.2026): Website oder Adresse im Onboarding → die KI
 * holt die öffentlichen Fakten und füllt offene Fragen. Nur Lücken.
 */
export async function recherchiereOnboarding(args: {
	id: string;
	fragen: { id: string; frage: string }[];
}): Promise<{
	verlauf: OnboardingVerlaufDto;
	uebernommen: number;
	quelle: { website: string | null; adresse: string | null } | null;
	quellen: string[];
}> {
	const { data, error } = await client.api
		.onboarding({ id: args.id })
		.recherchieren.post({ fragen: args.fragen });

	if (error !== null) {
		throw new Error(`Die Web-Recherche ist fehlgeschlagen (Status ${String(error.status)}).`);
	}

	return data;
}

export async function vorbefuelleOnboarding(args: {
	id: string;
	fragen: { id: string; frage: string }[];
}): Promise<{ verlauf: OnboardingVerlaufDto; uebernommen: number; dateien: number }> {
	const { data, error } = await client.api
		.onboarding({ id: args.id })
		.vorbefuellen.post({ fragen: args.fragen });

	if (error !== null) {
		throw new Error(
			`Das Vorbefüllen aus den Unterlagen ist fehlgeschlagen (Status ${String(error.status)}).`,
		);
	}

	return data;
}

/**
 * Die geteilte Drehvorbereitung hinter einem Token — die eine öffentliche
 * Datenquelle der App: Sie funktioniert bewusst ohne Anmeldung, weil
 * Drehbeteiligte ohne Konto sie lesen und unterschreiben sollen.
 */
export type DrehplanDatenDto = {
	kundeName: string;
	/** Der Änderungsverlauf — neuester Eintrag zuerst. */
	anpassungen: { datum: string; text: string }[];
	dienstleistungen: Dienstleistung[];
	gekauft: string | null;
	drehbuch: KonzeptInhalt["drehbuch"] | null;
	/** Die individuelle Drehtag-Planung aus dem Konzept — Ablauf, Vorbereitung, Checkliste. */
	drehplanung: NonNullable<KonzeptInhalt["drehplanung"]> | null;
	ort: string | null;
	termin: string | null;
	zeiten: string | null;
	/** Die Kundenfreigabe (16.09.2026) — wer wann „passt so" gesagt hat, sonst null. */
	kundenFreigabe: KundenFreigabeDto | null;
};

/** Wer die Drehvorbereitung wann freigegeben hat — `am` als ISO-String. */
export type KundenFreigabeDto = { name: string; am: string };

/** „Passt so": Der Kunde gibt die geteilte Drehvorbereitung mit seinem Namen frei. */
export async function sendeDrehplanFreigabe(args: {
	token: string;
	name: string;
}): Promise<KundenFreigabeDto> {
	const { data, error } = await client.api
		.drehplan({ token: args.token })
		.freigabe.post({ name: args.name });

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Die Freigabe konnte nicht gespeichert werden — bitte nochmal versuchen.",
			}),
		);
	}

	return data;
}

export async function fetchDrehplan(token: string): Promise<DrehplanDatenDto> {
	const { data, error } = await client.api.drehplan({ token }).get();

	if (error !== null) {
		throw new Error(
			error.status === 404
				? "Diesen Drehplan gibt es nicht (mehr)."
				: `Der Drehplan konnte nicht geladen werden (Status ${String(error.status)}).`,
		);
	}

	return data;
}

export function drehplanQuery(token: string) {
	return queryOptions({
		queryKey: ["drehplan", token],
		queryFn: () => fetchDrehplan(token),
	});
}

// ─── Marken-Unterlagen (#36) ────────────────────────────────────────────────

/**
 * Zwei Türen auf denselben Bereich: `mein` für Kundenkonten (die customerId
 * kommt serverseitig aus dem Kunden-Zugang, nie von hier), `kunde` für das
 * Team in der Kundenakte.
 */
export type MarkenZiel = { art: "mein" } | { art: "kunde"; customerId: string };

/** `kunde: null` heißt: Dieses Konto ist keinem Kunden zugeordnet. */
export type MeineMarkenUnterlagen = {
	kunde: { id: string; name: string } | null;
	dateien: BrandAssetDto[];
};

/** Base64-Teil der Data-URL — genau das Format, das das Backend erwartet. */
function dateiAlsBase64(datei: File): Promise<string> {
	return new Promise((res, rej) => {
		const reader = new FileReader();
		reader.onload = () => res(String(reader.result).split(",")[1] ?? "");
		reader.onerror = () => rej(new Error(`„${datei.name}“ konnte nicht gelesen werden.`));
		reader.readAsDataURL(datei);
	});
}

/* ---------- Profilbild (02.09.2026) ---------- */

/** Der Link zum Profilbild eines Kontos — `version` (der Speicher-Schlüssel) bricht den Browser-Cache. */
export function avatarUrl(args: { userId: string; version?: string | null }): string {
	const basis = `/api/konto/avatar/${encodeURIComponent(args.userId)}`;
	return args.version === undefined || args.version === null
		? basis
		: `${basis}?v=${encodeURIComponent(args.version)}`;
}

const AVATAR_KANTE = 512;

/**
 * Verkleinert das gewählte Bild im Browser auf höchstens 512 px (längste
 * Kante) und macht ein JPEG daraus — ein 12-MP-Handyfoto wird so zu ein
 * paar Dutzend KB, bevor es den Rechner verlässt.
 */
async function alsAvatarBild(datei: File): Promise<File> {
	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(datei);
	} catch {
		throw new Error(
			"Das ist kein Bild, das der Browser lesen kann — bitte PNG, JPEG oder WebP wählen.",
		);
	}
	const faktor = Math.min(1, AVATAR_KANTE / Math.max(bitmap.width, bitmap.height));
	const breite = Math.max(1, Math.round(bitmap.width * faktor));
	const hoehe = Math.max(1, Math.round(bitmap.height * faktor));
	const canvas = document.createElement("canvas");
	canvas.width = breite;
	canvas.height = hoehe;
	const ctx = canvas.getContext("2d");
	if (ctx === null) throw new Error("Das Bild konnte nicht verarbeitet werden.");
	ctx.drawImage(bitmap, 0, 0, breite, hoehe);
	bitmap.close();
	const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.88));
	if (blob === null) throw new Error("Das Bild konnte nicht verarbeitet werden.");
	return new File([blob], "profilbild.jpg", { type: "image/jpeg" });
}

export async function kontoAvatarHochladen(datei: File): Promise<{ key: string }> {
	const verkleinert = await alsAvatarBild(datei);
	const datenBase64 = await dateiAlsBase64(verkleinert);
	const { data, error } = await client.api.konto.avatar.put({ datenBase64 });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Profilbild konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function kontoAvatarLoeschen(): Promise<{ entfernt: boolean }> {
	const { data, error } = await client.api.konto.avatar.delete();
	if (error !== null) {
		throw new Error(
			`Das Profilbild konnte nicht entfernt werden (Status ${String(error.status)}).`,
		);
	}
	return data;
}

export async function fetchMeineMarkenUnterlagen(): Promise<MeineMarkenUnterlagen> {
	const { data, error } = await client.api.brand.mein.get();

	if (error !== null) {
		throw new Error(
			`Die Marken-Unterlagen konnten nicht geladen werden (Status ${String(error.status)}).`,
		);
	}

	return data;
}

export const meineMarkenUnterlagenQuery = queryOptions({
	queryKey: ["brand", "mein"],
	queryFn: fetchMeineMarkenUnterlagen,
});

export async function fetchMarkenUnterlagen(customerId: string): Promise<BrandAssetDto[]> {
	const { data, error } = await client.api.brand.kunden({ customerId }).get();

	if (error !== null) {
		throw new Error(
			`Die Marken-Unterlagen konnten nicht geladen werden (Status ${String(error.status)}).`,
		);
	}

	return data.dateien;
}

export function markenUnterlagenQuery(customerId: string) {
	return queryOptions({
		queryKey: ["brand", "kunde", customerId],
		queryFn: () => fetchMarkenUnterlagen(customerId),
	});
}

export async function ladeMarkenDateiHoch(args: {
	ziel: MarkenZiel;
	datei: File;
}): Promise<BrandAssetDto> {
	const body = {
		dateiname: args.datei.name,
		// Bei exotischen Endungen (.ai, .indd) meldet der Browser oft nichts.
		mimeTyp: args.datei.type === "" ? "application/octet-stream" : args.datei.type,
		datenBase64: await dateiAlsBase64(args.datei),
	};

	const { data, error } =
		args.ziel.art === "mein"
			? await client.api.brand.mein.dateien.post(body)
			: await client.api.brand.kunden({ customerId: args.ziel.customerId }).dateien.post(body);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `„${args.datei.name}“ konnte nicht hochgeladen werden.`,
			}),
		);
	}

	return data;
}

export async function loescheMarkenDatei(args: { ziel: MarkenZiel; id: string }): Promise<void> {
	const { error } =
		args.ziel.art === "mein"
			? await client.api.brand.mein.dateien({ id: args.id }).delete()
			: await client.api.brand.dateien({ id: args.id }).delete();

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({ value: error.value, fallback: "Die Datei konnte nicht gelöscht werden." }),
		);
	}
}

/**
 * Download läuft als normaler Link statt über Eden: Der Browser schickt den
 * Sitzungs-Cookie mit und bekommt die Datei als Attachment.
 */
export function markenDateiUrl(args: { ziel: MarkenZiel; id: string }): string {
	return args.ziel.art === "mein"
		? `/api/brand/mein/dateien/${args.id}`
		: `/api/brand/dateien/${args.id}`;
}

// ─── KI-Wissen (Marketing & Copy) ───────────────────────────────────────────

/**
 * Eugens Marketing-Wissen für die KI — wie Projekt-Kontexte bei Claude.
 * Aktive Bausteine fließen serverseitig in jede Konzept-Erstellung ein.
 */
export type WissenDto = {
	id: string;
	titel: string;
	inhalt: string;
	aktiv: boolean;
	updatedAt: string;
};

export async function fetchWissen(): Promise<WissenDto[]> {
	const { data, error } = await client.api.wissen.get();

	if (error !== null) {
		throw new Error(`Das KI-Wissen konnte nicht geladen werden (Status ${String(error.status)}).`);
	}

	return data;
}

export const wissenQuery = queryOptions({
	queryKey: ["ki-wissen"],
	queryFn: fetchWissen,
});

export async function speichereWissen(args: {
	id: string;
	daten: { titel: string; inhalt: string; aktiv: boolean };
}): Promise<WissenDto> {
	const { data, error } = await client.api.wissen({ id: args.id }).put(args.daten);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Wissens-Baustein konnte nicht gespeichert werden.",
			}),
		);
	}

	return data;
}

export async function loescheWissen(id: string): Promise<void> {
	const { error } = await client.api.wissen({ id }).delete();

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Wissens-Baustein konnte nicht gelöscht werden.",
			}),
		);
	}
}

// ─── Drehplan-Kommentare ────────────────────────────────────────────────────

/**
 * Ein Kommentar auf der geteilten Drehvorbereitung — Kunden und Beteiligte
 * schreiben ihn ohne Konto (der Teilen-Token ist der Zugang), das Team liest
 * ihn auf der Bearbeiten-Seite, setzt ihn mit der KI um und hakt ihn ab.
 */
export type DrehplanKommentarDto = {
	id: string;
	abschnittId: string;
	/** Der genauere Bezug im Abschnitt, vom Schreiber gewählt (z. B. "S-01, Geschäftsführung"). */
	bezug: string | null;
	autorName: string;
	text: string;
	erledigt: boolean;
	createdAt: string;
};

export function drehplanKommentareQuery(token: string) {
	return queryOptions({
		queryKey: ["drehplan-kommentare", token],
		queryFn: async (): Promise<DrehplanKommentarDto[]> => {
			const { data, error } = await client.api.drehplan({ token }).kommentare.get();
			if (error !== null) {
				throw new Error(
					`Die Kommentare konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function sendeDrehplanKommentar(args: {
	token: string;
	abschnittId: string;
	bezug?: string;
	autorName: string;
	text: string;
}): Promise<DrehplanKommentarDto> {
	const { data, error } = await client.api.drehplan({ token: args.token }).kommentare.post({
		abschnittId: args.abschnittId,
		...(args.bezug !== undefined ? { bezug: args.bezug } : {}),
		autorName: args.autorName,
		text: args.text,
	});

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Kommentar konnte nicht gesendet werden — bitte nochmal versuchen.",
			}),
		);
	}

	return data;
}

/** Die Team-Sicht auf dieselben Kommentare — hinter der Anmeldung. */
export function teamKommentareQuery(verlaufId: string) {
	return queryOptions({
		queryKey: ["team-kommentare", verlaufId],
		queryFn: async (): Promise<DrehplanKommentarDto[]> => {
			const { data, error } = await client.api.onboarding({ id: verlaufId }).kommentare.get();
			if (error !== null) {
				throw new Error(
					`Die Kommentare konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/**
 * Eine unterschriebene Einwilligung auf der geteilten Drehvorbereitung — die
 * Liste ist bewusst schmal (kein Wortlaut, keine Adresse, kein Bild): Alles
 * Weitere steckt im PDF, das der Server je Unterschrift baut.
 */
export type DrehplanUnterschriftDto = {
	id: string;
	name: string;
	rolle: string | null;
	createdAt: string;
};

export function drehplanUnterschriftenQuery(token: string) {
	return queryOptions({
		queryKey: ["drehplan-einwilligungen", token],
		queryFn: async (): Promise<DrehplanUnterschriftDto[]> => {
			const { data, error } = await client.api.drehplan({ token }).einwilligungen.get();
			if (error !== null) {
				throw new Error(
					`Die Einwilligungen konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function sendeDrehplanEinwilligung(args: {
	token: string;
	name: string;
	adresse: string;
	telefon?: string;
	rolle?: string;
	/** Das gezeichnete Unterschriftsbild als PNG-Data-URL. */
	bild: string;
}): Promise<DrehplanUnterschriftDto & { abrufToken: string }> {
	const { data, error } = await client.api.drehplan({ token: args.token }).einwilligungen.post({
		name: args.name,
		adresse: args.adresse,
		...(args.telefon !== undefined ? { telefon: args.telefon } : {}),
		...(args.rolle !== undefined ? { rolle: args.rolle } : {}),
		bild: args.bild,
	});

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Die Unterschrift konnte nicht gespeichert werden — bitte nochmal versuchen.",
			}),
		);
	}

	return data;
}

/**
 * Die Adresse des eigenen Einwilligungs-PDFs auf der offenen Seite — nur mit
 * dem Abruf-Schlüssel, den der Unterzeichner beim Unterschreiben bekommt
 * (Rundgang 12.09.2026). Ein normaler Link zum Herunterladen, kein Eden-Aufruf.
 */
export function drehplanEinwilligungPdfUrl(args: {
	token: string;
	einwilligungId: string;
	abruf: string;
}): string {
	return `/api/drehplan/${encodeURIComponent(args.token)}/einwilligungen/${encodeURIComponent(args.einwilligungId)}/pdf?abruf=${encodeURIComponent(args.abruf)}`;
}

/** Das Einwilligungs-PDF fürs Team — über den Verlauf, hinter dem Login. */
export function teamEinwilligungPdfUrl(args: {
	verlaufId: string;
	einwilligungId: string;
}): string {
	return `/api/onboarding/${encodeURIComponent(args.verlaufId)}/einwilligungen/${encodeURIComponent(args.einwilligungId)}/pdf`;
}

/** Das Einwilligungs-PDF für den angemeldeten Kunden — nur seine eigenen Drehs. */
export function meineEinwilligungPdfUrl(einwilligungId: string): string {
	return `/api/mein/einwilligungen/${encodeURIComponent(einwilligungId)}/pdf`;
}

/**
 * Die Einwilligungen des EIGENEN Kunden — für die Kundenansicht. `kunde:
 * false` heißt: Das Konto ist keinem Kunden zugeordnet (Team-Konto oder noch
 * nicht eingerichtet); die Karte zeigt dann den Hinweis-Zustand.
 */
export const meineEinwilligungenQuery = queryOptions({
	queryKey: ["meine-einwilligungen"],
	queryFn: async (): Promise<{
		kunde: boolean;
		drehs: {
			verlaufId: string;
			verlaufName: string;
			teilenToken: string | null;
			einwilligungen: DrehplanUnterschriftDto[];
		}[];
	}> => {
		const { data, error } = await client.api.mein.einwilligungen.get();
		if (error !== null) {
			throw new Error(
				`Die Einwilligungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

/**
 * Das Onboarding aus Kundensicht (02.09.2026): der Rahmen, aus dem der
 * Browser den Fragenkatalog baut (`lib/onboarding.ts`), die eigenen
 * Vorab-Antworten und ob das Team schon ausgewertet hat.
 */
export type OnboardingVorabDto = {
	verlaufId: string;
	name: string;
	kundenart: "direktkunde" | "agentur";
	dienstleistungen: Dienstleistung[];
	tiefe: "kompakt" | "ausfuehrlich";
	gekauft: string | null;
	kundenAntworten: Record<string, string>;
	kundenAntwortenAm: string | null;
	ausgewertet: boolean;
};

export const meinOnboardingQuery = queryOptions({
	queryKey: ["mein-onboarding"],
	queryFn: async (): Promise<{ kunde: boolean; verlaeufe: OnboardingVorabDto[] }> => {
		const { data, error } = await client.api.mein.onboarding.get();
		if (error !== null) {
			throw new Error(
				`Die Onboarding-Fragen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function speichereMeineOnboardingAntworten(args: {
	verlaufId: string;
	antworten: Record<string, string>;
}): Promise<OnboardingVorabDto> {
	const { data, error } = await client.api.mein
		.onboarding({ id: args.verlaufId })
		.antworten.put({ antworten: args.antworten });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Antworten konnten nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

// ─── Benachrichtigungen (die Glocke) ────────────────────────────────────────

export type BenachrichtigungDto = {
	id: string;
	art:
		| "feedback"
		| "einwilligung"
		| "konzept"
		| "projekt"
		| "erinnerung"
		| "beitrag"
		| "chat"
		| "vertrieb";
	/** Eingangs-Gruppe der Glocke (Vokabular, T016). */
	kategorie: "feedback" | "produktion" | "ki" | "erinnerung" | "chat" | "vertrieb";
	titel: string;
	text: string | null;
	link: string | null;
	/** null = fürs ganze Team; sonst die userId des Empfängers („Für dich"). */
	empfaengerUserId: string | null;
	/** Aus Sicht des eigenen Kontos — gelesen gilt je Person (T016). */
	gelesen: boolean;
	createdAt: string;
};

/**
 * Die Team-Glocke — pollt im Hintergrund, damit neues Kundenfeedback und
 * unterschriebene Einwilligungen ohne Neuladen auftauchen.
 */
export const benachrichtigungenQuery = queryOptions({
	queryKey: ["benachrichtigungen"],
	queryFn: async (): Promise<{ eintraege: BenachrichtigungDto[]; ungelesen: number }> => {
		const { data, error } = await client.api.benachrichtigungen.get();
		if (error !== null) {
			throw new Error(
				`Die Benachrichtigungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	refetchInterval: 60_000,
});

export async function markiereAlleGelesen(): Promise<void> {
	const { error } = await client.api.benachrichtigungen.gelesen.post();
	if (error !== null) {
		throw new Error(`Als gelesen markieren schlug fehl (Status ${String(error.status)}).`);
	}
}

/** Die Team-Sicht auf die Einwilligungen eines Verlaufs — hinter der Anmeldung. */
export function teamEinwilligungenQuery(verlaufId: string) {
	return queryOptions({
		queryKey: ["team-einwilligungen", verlaufId],
		queryFn: async (): Promise<DrehplanUnterschriftDto[]> => {
			const { data, error } = await client.api.onboarding({ id: verlaufId }).einwilligungen.get();
			if (error !== null) {
				throw new Error(
					`Die Einwilligungen konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function setzeKommentarErledigt(args: {
	verlaufId: string;
	kommentarId: string;
	erledigt: boolean;
}): Promise<void> {
	const { error } = await client.api
		.onboarding({ id: args.verlaufId })
		.kommentare({ kommentarId: args.kommentarId })
		.patch({ erledigt: args.erledigt });

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: "Der Kommentar konnte nicht abgehakt werden.",
			}),
		);
	}
}

/**
 * Setzt das Kundenfeedback in einer überarbeiteten Konzept-Fassung um. Das
 * Ergebnis ist ein ENTWURF für den Team-Editor — gespeichert (und damit für
 * den Kunden sichtbar) wird erst, wenn das Team es freigibt.
 */
export async function ueberarbeiteKonzept(args: {
	kundeName: string;
	transkript?: string;
	konzept: KonzeptInhalt;
	kommentare: { abschnittId: string; bezug?: string; autorName: string; text: string }[];
}): Promise<KonzeptAntwort> {
	const { data, error } = await client.api.concepts.ueberarbeiten.post(args);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die KI konnte das Feedback nicht umsetzen (Status ${String(error.status)}). Versuch es gleich nochmal.`,
			}),
		);
	}

	return data;
}

// ─── Konzept-Erstellung (KI) ────────────────────────────────────────────────

/**
 * Schickt die im Onboarding-Gespräch erfassten Antworten an das Backend, das
 * daraus über OpenRouter Zusammenfassung und Strategiepapier erstellt. Läuft
 * dort kein Schlüssel, antwortet das Backend mit einer gekennzeichneten
 * Demo-Fassung (`quelle: "demo"`) — die Oberfläche zeigt das ehrlich an.
 */
export async function erstelleKonzept(auftrag: KonzeptAuftrag): Promise<KonzeptAntwort> {
	const { data, error } = await client.api.concepts.post(auftrag);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die KI konnte das Konzept nicht erstellen (Status ${String(error.status)}). Versuch es gleich nochmal.`,
			}),
		);
	}

	return data;
}

/**
 * Schickt die Gesprächsaufnahme (Base64 aus dem MediaRecorder) an das
 * Backend, das sie über ein Audio-Modell wörtlich transkribiert. Bei einer
 * Aufnahme ohne verständliche Sprache antwortet das Backend mit 422 und
 * einem Satz, der hier als Fehlermeldung durchgereicht wird.
 */
export async function transkribiereAufnahme(args: {
	kundeName: string;
	audioBase64: string;
	/** Audio-Container plus Video-Mitschnitte (Meet, Handy) — das Backend zieht die Tonspur heraus. */
	mimeTyp:
		| "audio/webm"
		| "audio/mp4"
		| "audio/ogg"
		| "audio/wav"
		| "audio/mpeg"
		| "audio/aac"
		| "audio/flac"
		| "video/mp4"
		| "video/quicktime"
		| "video/webm";
}): Promise<{ transkript: string; quelle: "openrouter" | "demo" }> {
	const { data, error } = await client.api.concepts.transcribe.post(args);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Aufnahme konnte nicht transkribiert werden (Status ${String(error.status)}). Die Aufnahme bleibt erhalten — versuch es gleich nochmal.`,
			}),
		);
	}

	return { transkript: data.transkript, quelle: data.quelle };
}

/**
 * Neue Varianten für einen Drehbuch-Baustein — fürs Team beim Bearbeiten
 * der Drehvorbereitung. Die KI hebt sich von den bestehenden Texten ab und
 * nutzt Transkript-Fakten plus das gepflegte Marketing-Wissen.
 */
export type VariantenVorschlag = {
	sprecher: string | null;
	titel: string | null;
	text: string;
};

export async function erstelleVarianten(args: {
	kundeName: string;
	art: "hooks" | "cta" | "reels";
	transkript?: string;
	bestehend: string[];
	anzahl: number;
}): Promise<{ vorschlaege: VariantenVorschlag[]; quelle: "openrouter" | "demo" }> {
	const { data, error } = await client.api.concepts.varianten.post(args);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die KI konnte keine Varianten erstellen (Status ${String(error.status)}). Versuch es gleich nochmal.`,
			}),
		);
	}

	return data;
}

/**
 * Der Assistent: beantwortet Fragen aus dem Datenbestand, den das Backend je
 * Rolle zusammenstellt (T083) — Kunden, Projekte, Onboardings, Termine,
 * Inhalte; für Kundenkonten nur die eigenen. Der Verlauf reist für
 * Rückfragen mit.
 */
export type AssistentVerlauf = { von: "team" | "ki"; text: string }[];
export type AssistentAntwort = { antwort: string; quelle: "openrouter" | "demo" };

function assistentFehler(error: { status: number; value: string | number | object | null }): Error {
	return new Error(
		fehlermeldungVon({
			value: error.value,
			fallback: `Die KI konnte gerade nicht antworten (Status ${String(error.status)}). Versuch es gleich nochmal.`,
		}),
	);
}

/** Team: der Assistent über den vollen Bestand — den Kontext baut das Backend (T083). */
export async function frageAssistent(args: {
	verlauf: AssistentVerlauf;
	frage: string;
}): Promise<AssistentAntwort> {
	const { data, error } = await client.api.assistent.chat.post(args);
	if (error !== null) throw assistentFehler(error);
	return data;
}

/** Team: die Kunden-Fassung simulieren — derselbe Kontext, den das Kundenkonto bekäme (T084). */
export async function simuliereAssistent(args: {
	customerId: string;
	verlauf: AssistentVerlauf;
	frage: string;
}): Promise<AssistentAntwort> {
	const { data, error } = await client.api.assistent.simulieren.post(args);
	if (error !== null) throw assistentFehler(error);
	return data;
}

/** Kundenkonto: der Assistent nur über die eigenen Daten (Kundengrenze, #38). */
export async function frageAssistentAlsKunde(args: {
	verlauf: AssistentVerlauf;
	frage: string;
}): Promise<AssistentAntwort> {
	const { data, error } = await client.api.mein.assistent.chat.post(args);
	if (error !== null) throw assistentFehler(error);
	return data;
}

export type AssistentVorschlagDto = {
	id: string;
	text: string;
	zielgruppe: "team" | "kunde";
	reihenfolge: number;
};

/** Die Vorschläge für eine Zielgruppe — gepflegt oder Standard. */
export function assistentVorschlaegeQuery(zielgruppe: "team" | "kunde") {
	return queryOptions({
		queryKey: ["assistent", "vorschlaege", zielgruppe],
		queryFn: async (): Promise<string[]> => {
			const { data, error } = await client.api.assistent.vorschlaege.fuer({ zielgruppe }).get();
			if (error !== null) throw assistentFehler(error);
			return data;
		},
	});
}

export const meineAssistentVorschlaegeQuery = queryOptions({
	queryKey: ["mein", "assistent", "vorschlaege"],
	queryFn: async (): Promise<string[]> => {
		const { data, error } = await client.api.mein.assistent.vorschlaege.get();
		if (error !== null) throw assistentFehler(error);
		return data;
	},
});

/** Team: alle gepflegten Vorschläge (Einstellungen). */
export const alleAssistentVorschlaegeQuery = queryOptions({
	queryKey: ["assistent", "vorschlaege", "alle"],
	queryFn: async (): Promise<AssistentVorschlagDto[]> => {
		const { data, error } = await client.api.assistent.vorschlaege.get();
		if (error !== null) throw assistentFehler(error);
		return data;
	},
});

export async function assistentVorschlagSpeichern(args: {
	id: string;
	text: string;
	zielgruppe: "team" | "kunde";
	reihenfolge?: number;
}): Promise<AssistentVorschlagDto> {
	const { data, error } = await client.api.assistent.vorschlaege({ id: args.id }).put({
		text: args.text,
		zielgruppe: args.zielgruppe,
		...(args.reihenfolge === undefined ? {} : { reihenfolge: args.reihenfolge }),
	});
	if (error !== null) throw assistentFehler(error);
	return data;
}

export async function assistentVorschlagLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.assistent.vorschlaege({ id: args.id }).delete();
	if (error !== null) throw assistentFehler(error);
}

/**
 * Fragt die KI zum aufgenommenen Onboarding-Gespräch — sie antwortet
 * ausschließlich aus dem Transkript. Der bisherige Verlauf reist mit, damit
 * Rückfragen im Zusammenhang verstanden werden.
 */
export async function frageZumOnboarding(args: {
	kundeName: string;
	transkript: string;
	verlauf: { von: "team" | "ki"; text: string }[];
	frage: string;
}): Promise<{ antwort: string; quelle: "openrouter" | "demo" }> {
	const { data, error } = await client.api.concepts.chat.post(args);

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die KI konnte gerade nicht antworten (Status ${String(error.status)}). Versuch es gleich nochmal.`,
			}),
		);
	}

	return data;
}

// ─── Kundenbereich: „Projekt anfragen" & die „Mein Bereich"-Übersicht ────────
//
// Seit dem Rundgang vom 12.09.2026 (Issue #154) ist eine Projektanfrage aus
// dem Kundenbereich ein Interessent mit Kanal `kundenbereich` — dieselbe
// Pipeline wie Anfragen per E-Mail oder WhatsApp. Hier bleibt die Kundensicht.

import type {
	AnfrageDetails,
	AnfrageMeldungDto,
	AuftragArt,
	AuftragPaket,
	AuftragZweck,
} from "@olgen/api";

export type { AnfrageDetails, AnfrageMeldungDto, AuftragArt, AuftragPaket, AuftragZweck };

/**
 * Die Projekt-Phase in Kundensprache: Die frühen Stufen kommen aus dem
 * Onboarding-Verlauf, die hinteren (Dreh bis Fertig) aus dem echten Projekt.
 */
export type ProjektPhase =
	| "kickoff"
	| "gespraech-gefuehrt"
	| "drehvorbereitung"
	| "feedback"
	| "dreh"
	| "schnitt"
	| "freigabe"
	| "fertig";

export type ProjektStandDto = {
	/** Gesetzt, sobald ein echtes Projekt dahintersteht — sonst null. */
	projektId: string | null;
	verlaufId: string | null;
	name: string;
	dienstleistungen: Dienstleistung[];
	phase: ProjektPhase;
	drehtermin: string | null;
	teilenToken: string | null;
	/** Die Kundenfreigabe der Drehvorbereitung (16.09.2026) — null, solange sie aussteht. */
	drehvorbereitungFreigabe: KundenFreigabeDto | null;
	/** Eugens Zusage: erste Fassung 3–5 Werktage nach dem Drehtag ("YYYY-MM-DD"), sonst null. */
	ersteFassung: { von: string; bis: string } | null;
	/** Kick-off-Zusammenfassung — nur für Agenturen zu ihren Endkunden gefüllt. */
	zusammenfassung: string | null;
	aktualisiertAm: string;
	/** Review-Link und wie viele Inhalte dort auf Freigabe warten (T128). */
	reviewToken: string | null;
	offeneReviews: number;
};

export type MeineUebersichtDto =
	| { art: "kein-kundenkonto" }
	| {
			art: "direktkunde";
			kundeName: string;
			projekte: ProjektStandDto[];
			/** Eigene Projektanfragen — offene warten auf die Team-Bestätigung. */
			anfragen: AnfrageMeldungDto[];
			/** Die Projektleitung des jüngsten Projekts — das Gesicht im Hub. */
			ansprechpartner: { userId: string; name: string; email: string } | null;
	  }
	| {
			art: "agentur";
			kundeName: string;
			anfragen: AnfrageMeldungDto[];
			endkunden: {
				customerId: string;
				name: string;
				gekauft: string | null;
				projekte: ProjektStandDto[];
			}[];
	  };

/**
 * Die eine Übersicht des Kundenbereichs: Direktkunden sehen ihre Projekte,
 * Agenturen ihre Anfragen und Endkunden samt Phasen. Team-Konten bekommen
 * `kein-kundenkonto` — eine Antwort, kein Fehler.
 */
export async function fetchMeineUebersicht(): Promise<MeineUebersichtDto> {
	const { data, error } = await client.api.mein.uebersicht.get();

	if (error !== null) {
		throw new Error(`Die Übersicht konnte nicht geladen werden (Status ${String(error.status)}).`);
	}

	return data;
}

export const meineUebersichtQuery = queryOptions({
	queryKey: ["mein", "uebersicht"],
	queryFn: fetchMeineUebersicht,
});

export type AuftragMeldung = {
	paket?: AuftragPaket;
	art?: AuftragArt;
	wunschtermin?: string;
	kickoffTermin?: string;
	fuerWen: "endkunde" | "selbst";
	zweck?: AuftragZweck;
	testimonial: boolean;
	auftreten?: "whitelabel" | "extern";
	drehtage?: number;
	endkundeName?: string;
	ansprechpartner?: string;
	email?: string;
	telefon?: string;
	website?: string;
	adresse?: string;
	beschreibung?: string;
	bemerkungen?: string;
	infosVorhanden: boolean;
	/** Unterlagen (PDF/TXT/MD) als Base64 — nur Agentur-Konten, je max. 15 MB. */
	dateien?: { name: string; datenBase64: string }[];
};

/** Ein Kundenkonto fragt ein Projekt an — daraus wird ein Interessent in der Pipeline des Teams. */
export async function meldeAuftrag(args: AuftragMeldung): Promise<InteressentDto> {
	const { data, error } = await client.api.mein.auftraege.post({
		...args,
		// Serverseitig hat paket denselben Default — der abgeleitete Body-Typ
		// verlangt das Feld aber (zod-default = Pflicht im Ausgabetyp).
		paket: args.paket ?? "persoenlich",
	});

	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Anfrage konnte nicht gesendet werden (Status ${String(error.status)}).`,
			}),
		);
	}

	return data;
}

/** Download einer Unterlage zur Kundenbereich-Anfrage (Team) — der Cookie reist mit. */
export function interessentDateiUrl(args: { interessentId: string; dateiId: string }): string {
	return `/api/anfragen/${encodeURIComponent(args.interessentId)}/dateien/${encodeURIComponent(args.dateiId)}`;
}

/**
 * Die Team-Vorschau: dieselbe Übersicht, die ein Kunde in seinem Bereich
 * sieht — für einen benannten Kunden aus der Kundenliste.
 */
export function kundenUebersichtQuery(customerId: string) {
	return queryOptions({
		queryKey: ["kunden-uebersicht", customerId],
		queryFn: async (): Promise<MeineUebersichtDto> => {
			const { data, error } = await client.api.kunden({ customerId }).uebersicht.get();

			if (error !== null) {
				throw new Error(
					`Die Vorschau konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}

			return data;
		},
	});
}

// ─── Projekte & Aufgaben (der Produktionsbereich) ───────────────────────────

/** Die sechs internen Produktionsphasen — zugleich die Kanban-Spalten. */
export type ProjektPhaseIntern =
	| "kickoff"
	| "drehvorbereitung"
	| "dreh"
	| "schnitt"
	| "freigabe"
	| "abgeschlossen";

export type ProjektRolle = "projektleitung" | "videograf" | "cutter";

/** Die sechs Spalten von „Mein Board" (14.09.2026) — Projekt-Aufgaben nutzen dieselben Werte. */
export type AufgabeStatus =
	| "backlog"
	| "offen"
	| "in_arbeit"
	| "feedback"
	| "wartet_auf_kunde"
	| "erledigt";

export type AufgabeDto = {
	id: string;
	/** Null bei persönlichen Aufgaben (Mein Board). */
	projektId: string | null;
	/** Kunden-Bezug ohne Projekt — optional. */
	customerId: string | null;
	titel: string;
	beschreibung: string | null;
	/** Verknüpfungen (Drive, frame.io, Angebot …) — reine URLs. */
	links: string[];
	/** Null ohne Projekt. */
	phase: ProjektPhaseIntern | null;
	/** Null ohne Projekt. */
	rolle: ProjektRolle | null;
	status: AufgabeStatus;
	/** Die Person an der Aufgabe — gewinnt gegenüber der Rolle. */
	zustaendigUserId: string | null;
	erstelltVonUserId: string | null;
	/** Geteilt mit (@Name) — liegt auf jedem dieser Boards. */
	beteiligteUserIds: string[];
	/** "siri" = per Kurzbefehl angelegt (15.09.2026), sonst null. */
	quelle: string | null;
	reihenfolge: number;
	faelligAm: string | null;
	wartetSeit: string | null;
	erledigtAm: string | null;
	createdAt: string;
};

export type ProjektDto = {
	id: string;
	name: string;
	customerId: string;
	kundeName: string;
	/** White-Label-Kunde — die Oberfläche warnt vor direkter Kunden-Post. */
	whiteLabel: boolean;
	verlaufId: string | null;
	phase: ProjektPhaseIntern;
	dienstleistungen: Dienstleistung[];
	drehtermin: string | null;
	archiviertAm: string | null;
	/** Token des Review-Links (Etappe 3) — null, bis das Team zum ersten Mal teilt. */
	reviewToken: string | null;
	/** Das Schnitt-Briefing für den Cutter (T086) — KI-Entwurf, vom Team geprüft. */
	schnittBriefing: string | null;
	aufgabenGesamt: number;
	aufgabenErledigt: number;
	wartetAufKunde: number;
	createdAt: string;
	updatedAt: string;
};

export type ProjektDetailDto = ProjektDto & {
	aufgaben: AufgabeDto[];
	besetzung: { rolle: ProjektRolle; userId: string }[];
};

export type AufgabePersonDto = { id: string; name: string };

export type MeineAufgabeDto = AufgabeDto & {
	/** Null bei persönlichen Aufgaben. */
	projektName: string | null;
	/** Aus Projekt oder Kunden-Bezug, sonst leer. */
	kundeName: string;
	projektPhase: ProjektPhaseIntern | null;
	/** Liegt die Aufgabe in der aktuellen Projektphase (oder davor)? Persönliche sind immer dran. */
	dran: boolean;
	zustaendig: AufgabePersonDto | null;
	beteiligte: AufgabePersonDto[];
};

export type TeamMitgliedDto = {
	id: string;
	name: string;
	email: string;
	/** Bevorzugte Projektrolle (Team-Seite) — füllt neue Besetzungen vor. */
	standardRolle: ProjektRolle | null;
};

/** Ein Konto auf der Team-Seite (03.09.2026): Rolle, Sperre, letzter Login. */
export type TeamKontoDto = {
	id: string;
	name: string;
	email: string;
	teamRolle: TeamRolle;
	standardRolle: ProjektRolle | null;
	gesperrtAm: string | null;
	letzterLogin: string | null;
	erstelltAm: string;
};

export const teamKontenQuery = queryOptions({
	queryKey: ["team-konten"],
	queryFn: async (): Promise<TeamKontoDto[]> => {
		const { data, error } = await client.api.team.konten.get();
		if (error !== null) {
			throw new Error(`Das Team konnte nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

/** Inhaber: eine Person ins Team einladen — Konto + Einladungs-Mail mit Passwort-Link. */
export async function teamEinladen(args: {
	name: string;
	email: string;
	teamRolle: TeamRolle;
	standardRolle: ProjektRolle | null;
}): Promise<{ konto: TeamKontoDto; mailGesendet: boolean }> {
	const { data, error } = await client.api.team.konten.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Einladung konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Inhaber: Team-Rolle und Standardrolle einer Person ändern. */
export async function teamKontoAendern(args: {
	id: string;
	teamRolle?: TeamRolle;
	standardRolle?: ProjektRolle | null;
}): Promise<TeamKontoDto> {
	const { id, ...daten } = args;
	const { data, error } = await client.api.team.konten({ id }).patch(daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Änderung konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function teamKontoSperren(args: { id: string }): Promise<TeamKontoDto> {
	const { data, error } = await client.api.team.konten({ id: args.id }).sperren.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Konto konnte nicht gesperrt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function teamKontoEntsperren(args: { id: string }): Promise<TeamKontoDto> {
	const { data, error } = await client.api.team.konten({ id: args.id }).entsperren.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Konto konnte nicht entsperrt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function teamEinladungErneut(args: {
	id: string;
}): Promise<{ mailGesendet: boolean }> {
	const { data, error } = await client.api.team.konten({ id: args.id }).einladung.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Einladung konnte nicht gesendet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Inhaber: eine nie genutzte Einladung zurückziehen (das Konto verschwindet). */
export async function teamKontoEntfernen(args: { id: string }): Promise<void> {
	const { error } = await client.api.team.konten({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Konto konnte nicht entfernt werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export const projekteQuery = queryOptions({
	queryKey: ["projekte"],
	queryFn: async (): Promise<ProjektDto[]> => {
		const { data, error } = await client.api.projekte.get();
		if (error !== null) {
			throw new Error(
				`Die Projekte konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export function projektQuery(id: string) {
	return queryOptions({
		queryKey: ["projekte", id],
		queryFn: async (): Promise<ProjektDetailDto> => {
			const { data, error } = await client.api.projekte({ id }).get();
			if (error !== null) {
				throw new Error(
					error.status === 404
						? "Dieses Projekt gibt es nicht (mehr)."
						: `Das Projekt konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/** Das persönliche Dashboard: alle offenen Aufgaben der eigenen Rollen. */
export const meineAufgabenQuery = queryOptions({
	queryKey: ["meine-aufgaben"],
	queryFn: async (): Promise<MeineAufgabeDto[]> => {
		const { data, error } = await client.api["meine-aufgaben"].get();
		if (error !== null) {
			throw new Error(
				`Deine Aufgaben konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export const teamMitgliederQuery = queryOptions({
	queryKey: ["team-mitglieder"],
	queryFn: async (): Promise<TeamMitgliedDto[]> => {
		const { data, error } = await client.api.team.mitglieder.get();
		if (error !== null) {
			throw new Error(
				`Die Team-Liste konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function erstelleProjekt(args: {
	name: string;
	customerId: string;
	dienstleistungen?: Dienstleistung[];
	drehtermin?: string | null;
}): Promise<ProjektDetailDto> {
	const { data, error } = await client.api.projekte.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Projekt konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function aendereProjekt(args: {
	id: string;
	daten: {
		name?: string;
		phase?: ProjektPhaseIntern;
		drehtermin?: string | null;
		verlaufId?: string | null;
	};
}): Promise<ProjektDto> {
	const { data, error } = await client.api.projekte({ id: args.id }).patch(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Projekt konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function archiviereProjekt(args: { id: string }): Promise<void> {
	const { error } = await client.api.projekte({ id: args.id }).archivieren.post();
	if (error !== null) {
		throw new Error(`Das Archivieren schlug fehl (Status ${String(error.status)}).`);
	}
}

export async function setzeBesetzung(args: {
	projektId: string;
	rolle: ProjektRolle;
	userId: string;
}): Promise<void> {
	const { error } = await client.api
		.projekte({ id: args.projektId })
		.besetzung.post({ rolle: args.rolle, userId: args.userId });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Besetzung konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function erstelleAufgabe(args: {
	projektId: string;
	titel: string;
	phase: ProjektPhaseIntern;
	rolle: ProjektRolle;
	beschreibung?: string | null;
	faelligAm?: string | null;
}): Promise<void> {
	const { projektId, ...body } = args;
	const { error } = await client.api.projekte({ id: projektId }).aufgaben.post(body);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Aufgabe konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Eine Aufgabe von „Mein Board" (14.09.2026): ohne Pflicht-Projekt, gehört dem Anleger oder der gewählten Person. */
export async function erstellePersoenlicheAufgabe(args: {
	titel: string;
	beschreibung?: string | null;
	status?: "backlog" | "offen" | "in_arbeit";
	faelligAm?: string | null;
	projektId?: string | null;
	customerId?: string | null;
	zustaendigUserId?: string | null;
	beteiligteUserIds?: string[];
	links?: string[];
}): Promise<AufgabeDto> {
	const { data, error } = await client.api.aufgaben.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Aufgabe konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data.aufgabe;
}

/** Ändert eine Aufgabe; die Antwort trägt die (ggf. neue) Projektphase mit — null ohne Projekt. */
export async function aendereAufgabe(args: {
	id: string;
	daten: {
		titel?: string;
		beschreibung?: string | null;
		phase?: ProjektPhaseIntern;
		rolle?: ProjektRolle;
		status?: AufgabeStatus;
		faelligAm?: string | null;
		customerId?: string | null;
		zustaendigUserId?: string | null;
		beteiligteUserIds?: string[];
		links?: string[];
	};
}): Promise<{ aufgabe: AufgabeDto; projektPhase: ProjektPhaseIntern | null }> {
	const { data, error } = await client.api.aufgaben({ id: args.id }).patch(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Aufgabe konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return { aufgabe: data.aufgabe, projektPhase: data.projektPhase };
}

export async function loescheAufgabe(args: { id: string }): Promise<void> {
	const { error } = await client.api.aufgaben({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(`Die Aufgabe konnte nicht gelöscht werden (Status ${String(error.status)}).`);
	}
}

// ─── Termine (Etappe 2: das eine Termin-Modell) ─────────────────────────────

export type TerminArtWaehlbar = "kickoff" | "dreh" | "abgabe" | "meeting" | "veroeffentlichung";
/** Dazu "belegt": Belegung aus einem externen Kalender (13.09.2026), nur lesend. */
export type TerminArt = TerminArtWaehlbar | "belegt" | "frist";

export type TerminDto = {
	id: string;
	projektId: string;
	projektName: string;
	kundeName: string;
	art: TerminArt;
	titel: string;
	/** ISO-Datum "YYYY-MM-DD". */
	datum: string;
	/** "HH:MM" — null heißt ganztägig. */
	zeit: string | null;
	ort: string | null;
	notiz: string | null;
	/** Bekommt der Kunden-Kontakt die Kalender-Einladung samt Updates? */
	mitKunde: boolean;
	/** Besetzung des Projekts — Filter „Termine von X" im Kalender. */
	besetzungUserIds: string[];
	createdAt: string;
};

/** Alle Termine über alle (aktiven) Projekte — die Kalender-Seite. */
export const termineQuery = queryOptions({
	queryKey: ["termine"],
	queryFn: async (): Promise<TerminDto[]> => {
		const { data, error } = await client.api.termine.get({ query: {} });
		if (error !== null) {
			throw new Error(`Die Termine konnten nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

/** Das Fristenbild (13.09.2026): Aufgaben-Fälligkeiten, ablaufende Angebote, Buchungen ohne Termin — lesend. */
export const fristenQuery = queryOptions({
	queryKey: ["kalender", "fristen"],
	queryFn: async (): Promise<TerminDto[]> => {
		const { data, error } = await client.api.kalender.fristen.get();
		if (error !== null) {
			throw new Error(`Die Fristen konnten nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
	staleTime: 60_000,
});

/* ---------- Termin buchen (13.09.2026, wie Calendly) ---------- */

export type EreignisArt = "kickoff" | "update" | "drehtag";
export type EreignisTyp = {
	art: EreignisArt;
	titel: string;
	dauerMinuten: number | null;
	beschreibung: string;
};
export type Gastgeber = {
	userId: string;
	name: string;
	slug: string | null;
	meetLink: string | null;
};
export type BuchungBestaetigung = {
	token: string;
	art: EreignisArt;
	titel: string;
	datum: string;
	zeit: string | null;
	bis: string | null;
	gastgeberName: string;
	meetLink: string | null;
	status: "gebucht" | "abgesagt";
};
export type Wochenplan = { tag: number; von: string; bis: string }[];
export type Verfuegbarkeit = {
	userId: string;
	wochenplan: Wochenplan;
	pufferMinuten: number;
	vorlaufStunden: number;
	horizontTage: number;
	rasterMinuten: 15 | 30 | 60;
	drehtagSperreAbStunden: number;
	meetLink: string | null;
	slug: string | null;
	aktiv: boolean;
};

export const kontoVerfuegbarkeitQuery = queryOptions({
	queryKey: ["konto", "verfuegbarkeit"],
	queryFn: async (): Promise<Verfuegbarkeit> => {
		const { data, error } = await client.api.konto.verfuegbarkeit.get();
		if (error !== null) {
			throw new Error(
				`Die Verfügbarkeit konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function kontoVerfuegbarkeitSpeichern(daten: {
	wochenplan: Wochenplan;
	pufferMinuten: number;
	vorlaufStunden: number;
	horizontTage: number;
	rasterMinuten: 15 | 30 | 60;
	drehtagSperreAbStunden: number;
	meetLink: string | null;
	slug: string | null;
	aktiv: boolean;
}): Promise<Verfuegbarkeit> {
	const { data, error } = await client.api.konto.verfuegbarkeit.put(daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Verfügbarkeit konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Ein Buchungs-Zugang: angemeldet (Kundenbereich) oder öffentlich über den Slug. */
export type BuchungsZugang = { art: "mein"; userId: string } | { art: "oeffentlich"; slug: string };

export const buchungGastgeberQuery = queryOptions({
	queryKey: ["buchung", "gastgeber"],
	queryFn: async (): Promise<{ gastgeber: Gastgeber[]; typen: EreignisTyp[] }> => {
		const { data, error } = await client.api.mein.buchung.gastgeber.get();
		if (error !== null) {
			throw new Error(`Die Buchung konnte nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

export function buchungOeffentlichQuery(slug: string) {
	return queryOptions({
		queryKey: ["buchung", "oeffentlich", slug],
		queryFn: async (): Promise<{
			gastgeber: { name: string; slug: string | null };
			typen: EreignisTyp[];
		}> => {
			const { data, error } = await client.api.buchen({ slug }).get();
			if (error !== null) {
				throw new Error(
					fehlermeldungVon({
						value: error.value,
						fallback: `Diesen Buchungslink gibt es nicht (Status ${String(error.status)}).`,
					}),
				);
			}
			return data;
		},
	});
}

export function buchungTageQuery(zugang: BuchungsZugang, art: EreignisArt, monat: string) {
	return queryOptions({
		queryKey: ["buchung", "tage", zugang, art, monat],
		queryFn: async (): Promise<string[]> => {
			const { data, error } =
				zugang.art === "mein"
					? await client.api.mein
							.buchung({ userId: zugang.userId })({ art })
							.tage.get({ query: { monat } })
					: await client.api.buchen({ slug: zugang.slug })({ art }).tage.get({ query: { monat } });
			if (error !== null) {
				throw new Error(
					`Die freien Tage konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
		staleTime: 60_000,
	});
}

export function buchungSlotsQuery(zugang: BuchungsZugang, art: EreignisArt, tag: string) {
	return queryOptions({
		queryKey: ["buchung", "slots", zugang, art, tag],
		queryFn: async (): Promise<string[]> => {
			const { data, error } =
				zugang.art === "mein"
					? await client.api.mein
							.buchung({ userId: zugang.userId })({ art })
							.slots.get({ query: { tag } })
					: await client.api.buchen({ slug: zugang.slug })({ art }).slots.get({ query: { tag } });
			if (error !== null) {
				throw new Error(
					`Die Zeitfenster konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
		staleTime: 30_000,
	});
}

export async function terminBuchen(
	zugang: BuchungsZugang,
	daten: {
		art: EreignisArt;
		datum: string;
		zeit: string | null;
		name: string;
		email: string;
		firma: string | null;
		notiz: string | null;
	},
): Promise<BuchungBestaetigung> {
	const { data, error } =
		zugang.art === "mein"
			? await client.api.mein.buchung({ userId: zugang.userId }).post(daten)
			: await client.api.buchen({ slug: zugang.slug }).post(daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Buchung hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export function buchungQuery(token: string) {
	return queryOptions({
		queryKey: ["buchung", "token", token],
		queryFn: async (): Promise<BuchungBestaetigung> => {
			const { data, error } = await client.api.buchung({ token }).get();
			if (error !== null) {
				throw new Error(`Diese Buchung gibt es nicht (mehr) (Status ${String(error.status)}).`);
			}
			return data;
		},
	});
}

export async function buchungAbsagen(token: string): Promise<BuchungBestaetigung> {
	const { data, error } = await client.api.buchung({ token }).absagen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Absage hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export const meineBuchungenQuery = queryOptions({
	queryKey: ["mein", "buchungen"],
	queryFn: async (): Promise<{ kunde: boolean; buchungen: BuchungBestaetigung[] }> => {
		const { data, error } = await client.api.mein.buchungen.get();
		if (error !== null) {
			throw new Error(
				`Die Buchungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

/* ---------- Externe Kalender (13.09.2026) ---------- */

export type ExternerKalenderDto = {
	id: string;
	name: string;
	adresseMaskiert: string;
	angelegtVon: string;
	zuletztAbgerufen: string | null;
	fehler: string | null;
	anzahl: number;
	createdAt: string;
};

export const externeKalenderQuery = queryOptions({
	queryKey: ["kalender-extern"],
	queryFn: async (): Promise<ExternerKalenderDto[]> => {
		const { data, error } = await client.api["kalender-extern"].get();
		if (error !== null) {
			throw new Error(
				`Die externen Kalender konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

/** Die Belegungen aus externen Kalendern als lesende Termin-Einträge (Art „belegt"). */
export const externeBelegungenQuery = queryOptions({
	queryKey: ["kalender-extern", "belegungen"],
	queryFn: async (): Promise<TerminDto[]> => {
		const { data, error } = await client.api["kalender-extern"].belegungen.get();
		if (error !== null) {
			throw new Error(
				`Die Belegungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	staleTime: 5 * 60_000,
});

export async function externenKalenderAnlegen(args: {
	name: string;
	url: string;
}): Promise<ExternerKalenderDto> {
	const { data, error } = await client.api["kalender-extern"].post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kalender konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function externenKalenderAbrufen(args: { id: string }): Promise<ExternerKalenderDto> {
	const { data, error } = await client.api["kalender-extern"]({ id: args.id }).abrufen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kalender konnte nicht abgerufen werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function externenKalenderLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api["kalender-extern"]({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(`Der Kalender konnte nicht entfernt werden (Status ${String(error.status)}).`);
	}
}

export function projektTermineQuery(projektId: string) {
	return queryOptions({
		queryKey: ["termine", "projekt", projektId],
		queryFn: async (): Promise<TerminDto[]> => {
			const { data, error } = await client.api.termine.get({ query: { projektId } });
			if (error !== null) {
				throw new Error(
					`Die Termine konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/**
 * Belegte Drehtage für den AvailabilityPicker — bewusst nur Datumswerte
 * (die Route gibt Kundenkonten nichts weiter heraus).
 */
export const drehtageQuery = queryOptions({
	queryKey: ["drehtage"],
	queryFn: async (): Promise<string[]> => {
		const { data, error } = await client.api.mein.drehtage.get();
		if (error !== null) {
			throw new Error(
				`Die belegten Drehtage konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data.tage;
	},
});

export async function erstelleTermin(args: {
	projektId: string;
	art: TerminArtWaehlbar;
	titel: string;
	datum: string;
	zeit?: string | null;
	ort?: string | null;
	notiz?: string | null;
	mitKunde?: boolean;
}): Promise<TerminDto> {
	const { data, error } = await client.api.termine.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Termin konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function aendereTermin(args: {
	id: string;
	daten: {
		art?: TerminArtWaehlbar;
		titel?: string;
		datum?: string;
		zeit?: string | null;
		ort?: string | null;
		notiz?: string | null;
		mitKunde?: boolean;
	};
	/** Beim Verschieben abgefragt — geht in Notiz, Kalender-Mails und Log. */
	verschiebeGrund?: string;
}): Promise<TerminDto> {
	const { data, error } = await client.api.termine({ id: args.id }).patch({
		...args.daten,
		...(args.verschiebeGrund === undefined ? {} : { verschiebeGrund: args.verschiebeGrund }),
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Termin konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Stornieren wie bei Calendly: mit Grund — die Absage geht an die Kalender. */
export async function storniereTermin(args: { id: string; grund: string }): Promise<void> {
	const { error } = await client.api
		.termine({ id: args.id })
		.stornieren.post({ grund: args.grund });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Termin konnte nicht storniert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

// ─── Erinnerungs-Engine: das Zustell-Log (T037) ─────────────────────────────

export type GesendeteNachrichtDto = {
	id: string;
	katalogId: string;
	/** "mail" | "app" — "app" ist die Glocke. */
	kanal: "mail" | "app";
	empfaenger: string;
	betreff: string;
	bezug: string | null;
	gesendetAm: string;
};

/** Jede automatisch versendete Nachricht, neueste zuerst (LS: „Gesendete E-Mails"). */
export const gesendeteNachrichtenQuery = queryOptions({
	queryKey: ["gesendete-nachrichten"],
	queryFn: async (): Promise<GesendeteNachrichtDto[]> => {
		const { data, error } = await client.api["gesendete-nachrichten"].get();
		if (error !== null) {
			throw new Error(
				`Das Zustell-Log konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

// ─── Konzept-Erstellung als Server-Job (T034, #47) ──────────────────────────

/** Legt den Konzept-Job an; der Fragenkatalog reist mit (er lebt im Frontend). */
export async function starteKonzeptAuftrag(args: {
	id: string;
	fragen: { id: string; frage: string }[];
}): Promise<void> {
	const { error } = await client.api
		.onboarding({ id: args.id })
		["konzept-auftrag"].post({ fragen: args.fragen });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Konzept-Erstellung konnte nicht gestartet werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function fetchKonzeptStatus(id: string): Promise<{
	status: "offen" | "laeuft" | "erledigt" | "fehlgeschlagen" | null;
	fehler: string | null;
}> {
	const { data, error } = await client.api.onboarding({ id })["konzept-status"].get();
	if (error !== null) {
		throw new Error(`Der Konzept-Status ist nicht abrufbar (Status ${String(error.status)}).`);
	}
	return data;
}

/** Verlauf-Ids, an denen die KI gerade arbeitet — Wiederentdeckung nach Reload. */
export async function fetchLaufendeKonzeptJobs(): Promise<string[]> {
	const { data, error } = await client.api.onboarding["konzept-jobs"].get();
	if (error !== null) {
		throw new Error(`Die laufenden KI-Jobs sind nicht abrufbar (Status ${String(error.status)}).`);
	}
	return data.laufend;
}

// ─── Inhalte: Videos und Foto-Ordner am Projekt (Etappe 3, Paket 1) ─────────

export type InhaltTyp = "video" | "fotos";
export type InhaltKategorie = "auslieferung" | "reserve";
export type InhaltStatus = "entwurf" | "beim_kunden" | "aenderung" | "in_arbeit" | "freigegeben";

export type InhaltDateiDto = {
	id: string;
	dateiname: string;
	/** Bytes. */
	groesse: number;
	createdAt: string;
};

export type KommentarStatus = "offen" | "erledigt";

export type InhaltKommentarDto = {
	id: string;
	versionId: string;
	autorName: string;
	text: string;
	/** Sekunden im Video; bis = null heißt Punkt-Kommentar, beide null „betrifft das Ganze". */
	vonSekunde: number | null;
	bisSekunde: number | null;
	status: KommentarStatus;
	/** Kurze Team-Antwort (z. B. warum etwas bewusst anders gelöst wurde). */
	antwort: string | null;
	erledigtAm: string | null;
	createdAt: string;
};

export type InhaltVersionDto = {
	id: string;
	nummer: number;
	/** Der optionale „Was ist neu"-Satz des Cutters. */
	hinweis: string | null;
	createdAt: string;
	dateien: InhaltDateiDto[];
	/** Chronologisch (älteste zuerst). */
	kommentare: InhaltKommentarDto[];
};

export type InhaltDto = {
	id: string;
	projektId: string;
	titel: string;
	typ: InhaltTyp;
	kategorie: InhaltKategorie;
	status: InhaltStatus;
	/** Neueste Version zuerst. */
	versionen: InhaltVersionDto[];
	/** Die gebündelte Änderungsliste für den Cutter (T087) — KI-Entwurf, vom Team geprüft. */
	aenderungsliste: string | null;
	createdAt: string;
};

export function projektInhalteQuery(projektId: string) {
	return queryOptions({
		queryKey: ["inhalte", projektId],
		queryFn: async (): Promise<InhaltDto[]> => {
			const { data, error } = await client.api.projekte({ id: projektId }).inhalte.get();
			if (error !== null) {
				throw new Error(
					`Die Inhalte konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function inhaltHochladen(args: {
	projektId: string;
	titel: string;
	typ: InhaltTyp;
	kategorie: InhaltKategorie;
	dateien: File[];
}): Promise<InhaltDto> {
	const { data, error } = await client.api.projekte({ id: args.projektId }).inhalte.post({
		titel: args.titel,
		typ: args.typ,
		kategorie: args.kategorie,
		dateien: args.dateien,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Upload ist fehlgeschlagen (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Der Stand eines Teil-Uploads, wie ihn das Backend liefert. */
export type UploadStandDto = {
	id: string;
	teilBytes: number;
	groesse: number;
	anzahlTeile: number;
	vorhanden: number[];
	status: "laeuft" | "fertig" | "abgebrochen";
};

const UPLOAD_MERKER = "olgen.upload.";

function uploadMerkerKey(datei: File): string {
	return `${UPLOAD_MERKER}${datei.name}.${String(datei.size)}.${String(datei.lastModified)}`;
}

async function jsonOderFehler<T>(antwort: Response, fallback: string): Promise<T> {
	let wert: unknown = null;
	try {
		wert = await antwort.json();
	} catch {
		wert = null;
	}
	if (antwort.ok) return wert as T;
	throw new Error(
		fehlermeldungVon({
			value: typeof wert === "object" ? wert : null,
			fallback: `${fallback} (Status ${String(antwort.status)}).`,
		}),
	);
}

/**
 * Video in Teilstücken hochladen (21.09.2026, Eugens 20-GB-Videos): Die
 * Datei geht in 32-MB-Teilen, jeder Teil ist ein kurzer Request mit bis zu
 * drei Versuchen. Der Upload-Stand liegt beim Backend; die Upload-Id merkt
 * sich der Browser je Datei (Name, Größe, Änderungsdatum) — wählt man nach
 * einem Abbruch dieselbe Datei erneut, schickt er nur die fehlenden Teile.
 * Der Fortschritt zählt fertige Teile (plus den laufenden anteilig).
 */
export async function inhaltVideoInTeilenHochladen(
	args: { datei: File; onFortschritt?: (anteil: number) => void; signal?: AbortSignal } & (
		| { projektId: string; titel: string; kategorie: InhaltKategorie }
		| { inhaltId: string; hinweis: string | null }
	),
): Promise<InhaltDto> {
	const merker = uploadMerkerKey(args.datei);
	let stand: UploadStandDto | null = null;

	// Fortsetzen? Der gemerkte Upload muss noch laufen und zur Datei passen.
	try {
		const alteId = window.localStorage.getItem(merker);
		if (alteId !== null) {
			const antwort = await fetch(`/api/uploads/${encodeURIComponent(alteId)}`);
			if (antwort.ok) {
				const alt = (await antwort.json()) as UploadStandDto;
				if (alt.status === "laeuft" && alt.groesse === args.datei.size) stand = alt;
			}
			if (stand === null) window.localStorage.removeItem(merker);
		}
	} catch {
		stand = null;
	}

	if (stand === null) {
		const antwort = await fetch("/api/uploads/video", {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({
				dateiname: args.datei.name,
				groesse: args.datei.size,
				...("projektId" in args
					? { projektId: args.projektId, titel: args.titel, kategorie: args.kategorie }
					: {
							inhaltId: args.inhaltId,
							...(args.hinweis === null ? {} : { hinweis: args.hinweis }),
						}),
			}),
			signal: args.signal ?? null,
		});
		stand = await jsonOderFehler<UploadStandDto>(antwort, "Der Upload konnte nicht starten");
		try {
			window.localStorage.setItem(merker, stand.id);
		} catch {
			// Ohne Speicher gibt es eben kein Fortsetzen — der Upload selbst läuft.
		}
	}

	const { id, teilBytes, anzahlTeile } = stand;
	const vorhanden = new Set(stand.vorhanden);
	const melden = (fertigeTeile: number, laufendAnteil: number) =>
		args.onFortschritt?.(Math.min(1, (fertigeTeile + laufendAnteil) / anzahlTeile));
	melden(vorhanden.size, 0);

	for (let nummer = 1; nummer <= anzahlTeile; nummer += 1) {
		if (vorhanden.has(nummer)) continue;
		const von = (nummer - 1) * teilBytes;
		const teil = args.datei.slice(von, Math.min(von + teilBytes, args.datei.size));
		let versuch = 0;
		for (;;) {
			versuch += 1;
			try {
				await new Promise<void>((resolve, reject) => {
					const xhr = new XMLHttpRequest();
					xhr.open("PUT", `/api/uploads/${encodeURIComponent(id)}/teile/${String(nummer)}`);
					xhr.setRequestHeader("content-type", "application/octet-stream");
					xhr.upload.onprogress = (e) => {
						if (e.lengthComputable) melden(vorhanden.size, e.loaded / e.total);
					};
					xhr.onerror = () => reject(new Error("Verbindung abgebrochen."));
					xhr.onabort = () => reject(new Error("Upload abgebrochen."));
					xhr.onload = () => {
						if (xhr.status >= 200 && xhr.status < 300) {
							resolve();
							return;
						}
						let wert: unknown = null;
						try {
							wert = JSON.parse(xhr.responseText);
						} catch {
							wert = null;
						}
						reject(
							new Error(
								fehlermeldungVon({
									value: typeof wert === "object" ? wert : null,
									fallback: `Teil ${String(nummer)} wurde nicht angenommen (Status ${String(xhr.status)}).`,
								}),
							),
						);
					};
					args.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
					xhr.send(teil);
				});
				break;
			} catch (fehler: unknown) {
				if (args.signal?.aborted === true || versuch >= 3) {
					throw fehler instanceof Error
						? new Error(
								`${fehler.message} Der Upload lässt sich fortsetzen: einfach dieselbe Datei noch einmal wählen.`,
							)
						: fehler;
				}
				// Kurz warten, dann derselbe Teil noch einmal (Netzwackler).
				await new Promise((r) => setTimeout(r, 1500 * versuch));
			}
		}
		vorhanden.add(nummer);
		melden(vorhanden.size, 0);
	}

	const abschluss = await fetch(`/api/uploads/${encodeURIComponent(id)}/abschliessen`, {
		method: "POST",
	});
	const inhalt = await jsonOderFehler<InhaltDto>(
		abschluss,
		"Der Upload ist angekommen, aber das Zusammensetzen ist gescheitert",
	);
	try {
		window.localStorage.removeItem(merker);
	} catch {
		// egal
	}
	return inhalt;
}

/**
 * Video als Strom hochladen (17.09.2026): Der Browser schickt die Datei als
 * nackten Body, das Backend streamt sie in den Speicher — so gehen
 * Masterdateien bis 2 GB, ohne den Server-RAM zu belegen. XMLHttpRequest
 * statt fetch, weil nur XHR den Upload-Fortschritt meldet (bei 700 MB will
 * man einen Balken sehen). Der Cookie reist wie immer mit.
 */
export function inhaltVideoHochladenStrom(
	args: { datei: File; onFortschritt?: (anteil: number) => void } & (
		| { projektId: string; titel: string; kategorie: InhaltKategorie }
		| { inhaltId: string; hinweis: string | null }
	),
): Promise<InhaltDto> {
	const suche = new URLSearchParams({ dateiname: args.datei.name });
	let url: string;
	if ("projektId" in args) {
		suche.set("titel", args.titel);
		suche.set("kategorie", args.kategorie);
		url = `/api/projekte/${encodeURIComponent(args.projektId)}/inhalte/video?${suche.toString()}`;
	} else {
		if (args.hinweis !== null) suche.set("hinweis", args.hinweis);
		url = `/api/inhalte/${encodeURIComponent(args.inhaltId)}/versionen/video?${suche.toString()}`;
	}
	return new Promise<InhaltDto>((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("POST", url);
		xhr.setRequestHeader("content-type", "application/octet-stream");
		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) args.onFortschritt?.(e.loaded / e.total);
		};
		xhr.onerror = () =>
			reject(new Error("Der Upload ist abgebrochen — Verbindung prüfen und nochmal versuchen."));
		xhr.onload = () => {
			let antwort: unknown = null;
			try {
				antwort = JSON.parse(xhr.responseText);
			} catch {
				antwort = null;
			}
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve(antwort as InhaltDto);
				return;
			}
			reject(
				new Error(
					fehlermeldungVon({
						value: typeof antwort === "object" ? antwort : null,
						fallback: `Der Upload ist fehlgeschlagen (Status ${String(xhr.status)}).`,
					}),
				),
			);
		};
		xhr.send(args.datei);
	});
}

export async function inhaltVersionHochladen(args: {
	inhaltId: string;
	hinweis: string | null;
	dateien: File[];
}): Promise<InhaltDto> {
	const { data, error } = await client.api.inhalte({ id: args.inhaltId }).versionen.post({
		...(args.hinweis === null ? {} : { hinweis: args.hinweis }),
		dateien: args.dateien,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Upload ist fehlgeschlagen (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function inhaltAendern(args: {
	id: string;
	daten: { titel?: string; kategorie?: InhaltKategorie; status?: InhaltStatus };
}): Promise<InhaltDto> {
	const { data, error } = await client.api.inhalte({ id: args.id }).patch(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Inhalt konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function inhaltLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.inhalte({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Inhalt konnte nicht gelöscht werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Download-Adresse einer Inhalt-Datei — der Browser nimmt den Cookie mit. */
export function inhaltDateiUrl(dateiId: string): string {
	return `/api/inhalte/dateien/${dateiId}`;
}

// ─── Review: die offene Kundensicht der Inhalte (Etappe 3, Paket 2) ──────────

/** Ein Inhalt, wie ihn die offene Review-Seite sieht — bewusst ohne interne Felder. */
export type ReviewInhalt = {
	id: string;
	titel: string;
	typ: InhaltTyp;
	status: InhaltStatus;
	/** Neueste Version zuerst — sie ist die aktive im Player. */
	versionen: InhaltVersionDto[];
	createdAt: string;
};

export type ReviewDaten = {
	projektName: string;
	inhalte: ReviewInhalt[];
	/** Das Erklärvideo „So gebt ihr Feedback" — null, wenn keins hinterlegt ist. */
	feedbackVideo: ErklaervideoDto | null;
	/** Ein Team-Konto schaut zu: Kommentieren geht, Freigeben zählt nicht (17.09.2026). */
	teamAnsicht: boolean;
};

export function reviewQuery(token: string) {
	return queryOptions({
		queryKey: ["review", token],
		queryFn: async (): Promise<ReviewDaten> => {
			const { data, error } = await client.api.review({ token }).get();
			if (error !== null) {
				throw new Error(
					error.status === 404
						? "Dieses Review gibt es nicht (mehr). Bitte fragt euren Ansprechpartner nach einem frischen Link."
						: `Das Review konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function reviewKommentarSenden(args: {
	token: string;
	versionId: string;
	autorName: string;
	text: string;
	vonSekunde: number | null;
	bisSekunde: number | null;
}): Promise<InhaltKommentarDto> {
	const { data, error } = await client.api
		.review({ token: args.token })
		.versionen({ versionId: args.versionId })
		.kommentare.post({
			autorName: args.autorName,
			text: args.text,
			vonSekunde: args.vonSekunde,
			bisSekunde: args.bisSekunde,
		});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kommentar konnte nicht gesendet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function reviewInhaltFreigeben(args: {
	token: string;
	inhaltId: string;
	autorName: string;
}): Promise<void> {
	const { error } = await client.api
		.review({ token: args.token })
		.inhalte({ inhaltId: args.inhaltId })
		.freigeben.post({ autorName: args.autorName });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Freigabe ist nicht angekommen (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Datei-Adresse der offenen Review-Seite — Player und Fotos laden hierüber. */
export function reviewDateiUrl(token: string, dateiId: string): string {
	return `/api/review/${token}/dateien/${dateiId}`;
}

/** Foto-Ordner als ZIP — „komprimiert" verkleinert jedes Bild fürs Web (17.09.2026). */
export function reviewZipUrl(
	token: string,
	inhaltId: string,
	variante: "original" | "komprimiert",
): string {
	return `/api/review/${token}/inhalte/${inhaltId}/zip?variante=${variante}`;
}

/** Team: erzeugt (oder liefert) den einen Review-Link des Projekts. */
export async function reviewLinkErzeugen(projektId: string): Promise<{ token: string }> {
	const { data, error } = await client.api.projekte({ id: projektId })["review-link"].post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Review-Link konnte nicht erzeugt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

// ─── Das Freigabe-Uhrwerk (Etappe 3, Paket 3) ────────────────────────────────

/** Ein Inhalt auf der Kunden-Freigabenseite (T057). */
export type MeineFreigabe = {
	inhaltId: string;
	titel: string;
	typ: InhaltTyp;
	status: InhaltStatus;
	versionNummer: number;
	projektId: string;
	projektName: string;
	reviewToken: string | null;
	/** "YYYY-MM-DD" — Fälligkeit der Cutter-Aufgabe bei „wird überarbeitet". */
	fertigBis: string | null;
	aktualisiertAm: string;
	/** Dateien der neuesten Version — Download über `reviewDateiUrl` mit `download`-Attribut. */
	dateien: { id: string; dateiname: string; groesse: number }[];
};

/** Ein Termin aus Kundensicht (Hub, T070) — ohne interne Notiz. */
export type MeinTermin = {
	id: string;
	projektId: string;
	projektName: string;
	art: TerminArt;
	titel: string;
	datum: string;
	zeit: string | null;
	ort: string | null;
	/** Liegt vor heute — bleibt ein Jahr sichtbar („Gedreht am …", 17.09.2026). */
	vergangen: boolean;
};

/** Kundenkonto: die Termine der eigenen Projekte ab heute, chronologisch. */
export const meineTermineQuery = queryOptions({
	queryKey: ["mein", "termine"],
	queryFn: async (): Promise<{ kunde: boolean; termine: MeinTermin[] }> => {
		const { data, error } = await client.api.mein.termine.get();
		if (error !== null) {
			throw new Error(`Die Termine konnten nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

/** Ein Eintrag im Team-Eingang „Feedback & Wartendes" (T058). */
export type EingangEintrag = {
	inhaltId: string;
	titel: string;
	typ: InhaltTyp;
	status: InhaltStatus;
	versionNummer: number;
	projektId: string;
	projektName: string;
	kundeName: string;
	reviewToken: string | null;
	wartetSeit: string;
	offeneKommentare: number;
	letzterKommentar: {
		autorName: string;
		text: string;
		vonSekunde: number | null;
		createdAt: string;
	} | null;
	cutterUserId: string | null;
	projektleitungUserId: string | null;
	/** Die gebündelte Änderungsliste (T087), falls das Team eine erzeugt hat. */
	aenderungsliste: string | null;
};

/** Kundenkonto: die eigenen Inhalte im Review-Kreislauf. Team-Konten bekommen `kunde: false`. */
export const meineFreigabenQuery = queryOptions({
	queryKey: ["mein", "freigaben"],
	queryFn: async (): Promise<{ kunde: boolean; inhalte: MeineFreigabe[] }> => {
		const { data, error } = await client.api.mein.freigaben.get();
		if (error !== null) {
			throw new Error(
				`Die Freigaben konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	// Freigaben kommen über den offenen Review-Link — die Seite soll das ohne Reload merken.
	refetchInterval: 60_000,
});

/** Team: der Eingang — alles, was beim Kunden liegt oder als Feedback zurückkam. */
export const feedbackEingangQuery = queryOptions({
	queryKey: ["inhalte", "eingang"],
	queryFn: async (): Promise<EingangEintrag[]> => {
		const { data, error } = await client.api.inhalte.eingang.get();
		if (error !== null) {
			throw new Error(`Der Eingang konnte nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
	refetchInterval: 60_000,
});

// ─── Strategiepapier je Kunde (Etappe 5, T081) und KI-Lauf-Log (T088) ────────

export type StrategiepapierDto = {
	customerId: string;
	inhalt: string;
	status: "entwurf" | "freigegeben";
	freigegebenAm: string | null;
	updatedAt: string;
};

export function strategiepapierQuery(kundeId: string) {
	return queryOptions({
		queryKey: ["strategiepapier", kundeId],
		queryFn: async (): Promise<{ papier: StrategiepapierDto | null }> => {
			const { data, error } = await client.api
				.kunden({ customerId: kundeId })
				.strategiepapier.get();
			if (error !== null) {
				throw new Error(
					`Das Strategiepapier konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function strategiepapierSpeichern(args: {
	kundeId: string;
	inhalt: string;
}): Promise<StrategiepapierDto> {
	const { data, error } = await client.api
		.kunden({ customerId: args.kundeId })
		.strategiepapier.put({ inhalt: args.inhalt });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Papier konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function strategiepapierFreigabe(args: {
	kundeId: string;
	aktion: "freigeben" | "zurueckziehen";
}): Promise<StrategiepapierDto> {
	const pfad = client.api.kunden({ customerId: args.kundeId }).strategiepapier;
	const { data, error } =
		args.aktion === "freigeben" ? await pfad.freigeben.post() : await pfad.zurueckziehen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

// ─── Unterlagen für den KI-Entwurf des Strategiepapiers (05.09.2026) ─────────

export type StrategieUnterlageArt = "strategie" | "auswertung";

export type StrategieUnterlageDto = {
	id: string;
	art: StrategieUnterlageArt;
	dateiname: string;
	groesse: number;
	createdAt: string;
};

export type StrategieUnterlagenStand = {
	unterlagen: StrategieUnterlageDto[];
	/** Der KI-Lauf ist eingeplant oder läuft — die Oberfläche pollt dann. */
	laeuft: boolean;
	/** false auf Systemen ohne OpenRouter-Schlüssel — der Knopf erklärt es dann. */
	kiVerfuegbar: boolean;
};

export function strategieUnterlagenQuery(kundeId: string) {
	return queryOptions({
		queryKey: ["strategiepapier", kundeId, "unterlagen"],
		queryFn: async (): Promise<StrategieUnterlagenStand> => {
			const { data, error } = await client.api
				.kunden({ customerId: kundeId })
				.strategiepapier.unterlagen.get();
			if (error !== null) {
				throw new Error(
					`Die Unterlagen konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
		// Solange die KI schreibt, alle 5 s nachsehen — danach Ruhe.
		refetchInterval: (query) => (query.state.data?.laeuft === true ? 5_000 : false),
	});
}

export async function strategieUnterlagenHochladen(args: {
	kundeId: string;
	art: StrategieUnterlageArt;
	dateien: { name: string; datenBase64: string }[];
}): Promise<StrategieUnterlageDto[]> {
	const { data, error } = await client.api
		.kunden({ customerId: args.kundeId })
		.strategiepapier.unterlagen.post({ art: args.art, dateien: args.dateien });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Unterlagen konnten nicht hochgeladen werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function strategieUnterlageLoeschen(args: {
	kundeId: string;
	unterlageId: string;
}): Promise<void> {
	const { error } = await client.api
		.kunden({ customerId: args.kundeId })
		.strategiepapier.unterlagen({ unterlageId: args.unterlageId })
		.delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Unterlage konnte nicht entfernt werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Download-Adresse einer Unterlage — der Cookie reist über `<a href>` mit. */
export function strategieUnterlageUrl(args: { kundeId: string; unterlageId: string }): string {
	return `/api/kunden/${encodeURIComponent(args.kundeId)}/strategiepapier/unterlagen/${encodeURIComponent(args.unterlageId)}`;
}

/** Startet den KI-Lauf, der aus den Unterlagen den Entwurf schreibt (Job). */
export async function strategieEntwurfStarten(args: { kundeId: string }): Promise<void> {
	const { error } = await client.api
		.kunden({ customerId: args.kundeId })
		.strategiepapier.entwurf.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der KI-Entwurf konnte nicht gestartet werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Kundenkonto: das freigegebene Strategiepapier — null ohne Freigabe. */
export const meinStrategiepapierQuery = queryOptions({
	queryKey: ["mein", "strategiepapier"],
	queryFn: async (): Promise<{ kunde: boolean; papier: StrategiepapierDto | null }> => {
		const { data, error } = await client.api.mein.strategiepapier.get();
		if (error !== null) {
			throw new Error(
				`Die Strategie konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export type KiLaufDto = {
	id: string;
	art: string;
	bezug: string | null;
	status: "fertig" | "fehler";
	zusammenfassung: string | null;
	fehler: string | null;
	dauerMs: number;
	createdAt: string;
};

/** Der System-Mail-Katalog (`mail/mail-katalog.ts`): welche automatische Mail wann rausgeht. */
export type MailKatalogEintragDto = {
	id: string;
	titel: string;
	wirdGesendetWenn: string;
	empfaengerTyp: "team" | "kunde";
};

export const mailKatalogQuery = queryOptions({
	queryKey: ["mail-katalog"],
	staleTime: 60 * 60 * 1000,
	queryFn: async (): Promise<MailKatalogEintragDto[]> => {
		const { data, error } = await client.api["mail-katalog"].get();
		if (error !== null) {
			throw new Error(
				`Der Mail-Katalog konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export const kiLaeufeQuery = queryOptions({
	queryKey: ["ki-laeufe"],
	queryFn: async (): Promise<KiLaufDto[]> => {
		const { data, error } = await client.api["ki-laeufe"].get();
		if (error !== null) {
			throw new Error(`Das KI-Log konnte nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

// ─── Schulungs-Ablage fürs Team (Etappe 4, T075) ─────────────────────────────

export type SchulungDto = {
	id: string;
	titel: string;
	beschreibung: string | null;
	dateiname: string;
	groesse: number;
	createdAt: string;
};

export const schulungenQuery = queryOptions({
	queryKey: ["schulungen"],
	queryFn: async (): Promise<SchulungDto[]> => {
		const { data, error } = await client.api.schulungen.get();
		if (error !== null) {
			throw new Error(
				`Die Schulungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function schulungHochladen(args: {
	titel: string;
	beschreibung: string | null;
	datei: File;
}): Promise<SchulungDto> {
	const { data, error } = await client.api.schulungen.post({
		titel: args.titel,
		...(args.beschreibung === null ? {} : { beschreibung: args.beschreibung }),
		datei: args.datei,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Upload ist fehlgeschlagen (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function schulungLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.schulungen({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(`Die Schulung konnte nicht gelöscht werden (Status ${String(error.status)}).`);
	}
}

/** Abspiel-/Download-Adresse — der Cookie reist mit, Kundenkonten bekommen 403. */
export function schulungDateiUrl(id: string): string {
	return `/api/schulungen/${id}/datei`;
}

/** Team: Zugang zum Kundenbereich anlegen (T072) — Konto, Zuordnung, Zugangs-Mail. */
export async function kundenZugangAnlegen(args: {
	customerId: string;
	email: string;
	name: string;
}): Promise<{ userId: string; mailGesendet: boolean }> {
	const { data, error } = await client.api
		.customers({ id: args.customerId })
		.zugaenge.post({ email: args.email, name: args.name });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Zugang konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Kundenkonto sperren oder entsperren (ausgeschiedene Ansprechpartner, 17.09.2026). */
export async function kundenZugangSperren(args: {
	customerId: string;
	userId: string;
	gesperrt: boolean;
}): Promise<void> {
	const ziel = client.api.customers({ id: args.customerId }).zugaenge({ userId: args.userId });
	const { error } = args.gesperrt ? await ziel.sperren.post() : await ziel.entsperren.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Zugang konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Team: erledigt-Haken und Antwort an einem Review-Kommentar. */
export async function inhaltKommentarErledigen(args: {
	kommentarId: string;
	status?: KommentarStatus;
	antwort?: string | null;
}): Promise<InhaltKommentarDto> {
	const { data, error } = await client.api.inhalte.kommentare({ id: args.kommentarId }).patch({
		...(args.status === undefined ? {} : { status: args.status }),
		...(args.antwort === undefined ? {} : { antwort: args.antwort }),
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kommentar konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/* ---------- Freigabekette der Drehvorbereitung (T085) ---------- */

function drehvorbereitungFehler(error: { status: number; value: string | number | object | null }) {
	return new Error(
		fehlermeldungVon({
			value: error.value,
			fallback: `Das hat nicht geklappt (Status ${String(error.status)}).`,
		}),
	);
}

/** „Speichern & teilen": plant die Veröffentlichung auf 09:00 des nächsten Werktags. */
export async function teileDrehvorbereitung(verlaufId: string): Promise<OnboardingVerlaufDto> {
	const { data, error } = await client.api.onboarding({ id: verlaufId }).teilen.post();
	if (error !== null) throw drehvorbereitungFehler(error);
	return data;
}

/** Überspringt den Tagespuffer bewusst — sofort beim Kunden sichtbar. */
export async function veroeffentlicheDrehvorbereitung(
	verlaufId: string,
): Promise<OnboardingVerlaufDto> {
	const { data, error } = await client.api.onboarding({ id: verlaufId }).veroeffentlichen.post();
	if (error !== null) throw drehvorbereitungFehler(error);
	return data;
}

/* ---------- KI-Entwürfe für die Produktion (T086/T087) ---------- */

export type EntwurfAntwort = { text: string | null; quelle?: "openrouter" | "demo" };

function entwurfFehler(error: { status: number; value: string | number | object | null }) {
	return new Error(
		fehlermeldungVon({
			value: error.value,
			fallback: `Der Entwurf konnte nicht erstellt werden (Status ${String(error.status)}).`,
		}),
	);
}

/** Lässt die KI das Schnitt-Briefing entwerfen und speichert es als Vorschlag am Projekt. */
export async function schnittBriefingEntwerfen(projektId: string): Promise<EntwurfAntwort> {
	const { data, error } = await client.api
		.projekte({ id: projektId })
		["schnitt-briefing"].entwurf.post();
	if (error !== null) throw entwurfFehler(error);
	return data;
}

/** Speichert die vom Team geprüfte Fassung (null löscht sie). */
export async function schnittBriefingSpeichern(args: {
	projektId: string;
	text: string | null;
}): Promise<EntwurfAntwort> {
	const { data, error } = await client.api
		.projekte({ id: args.projektId })
		["schnitt-briefing"].put({ text: args.text });
	if (error !== null) throw entwurfFehler(error);
	return data;
}

/** Bündelt die offenen Kommentare der aktuellen Version zur Änderungsliste (Entwurf). */
export async function aenderungslisteEntwerfen(inhaltId: string): Promise<EntwurfAntwort> {
	const { data, error } = await client.api.inhalte({ id: inhaltId }).aenderungsliste.entwurf.post();
	if (error !== null) throw entwurfFehler(error);
	return data;
}

export async function aenderungslisteSpeichern(args: {
	inhaltId: string;
	text: string | null;
}): Promise<EntwurfAntwort> {
	const { data, error } = await client.api
		.inhalte({ id: args.inhaltId })
		.aenderungsliste.put({ text: args.text });
	if (error !== null) throw entwurfFehler(error);
	return data;
}

/* ---------- Kopilot (T124/T125) ---------- */

export type MorgenUeberblick = { text: string; quelle: "openrouter" | "demo"; datum: string };

/** „Dein Tag" als ein Absatz — einmal je Tag durch die KI, danach aus dem Speicher. */
export const morgenUeberblickQuery = queryOptions({
	queryKey: ["kopilot", "morgen"],
	queryFn: async (): Promise<MorgenUeberblick> => {
		const { data, error } = await client.api.kopilot.morgen.get();
		if (error !== null) {
			throw new Error(
				`Der Überblick konnte nicht erstellt werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	staleTime: 5 * 60 * 1000,
});

/** Review-Seite: Stichworte zu einem ausformulierten Kommentar-Entwurf (der Kunde sendet bewusst). */
export async function reviewKommentarHilfe(args: {
	token: string;
	versionId: string;
	stichworte: string;
	vonSekunde: number | null;
	bisSekunde: number | null;
}): Promise<{ text: string; quelle: "openrouter" | "demo" }> {
	const { data, error } = await client.api.review({ token: args.token })["kommentar-hilfe"].post({
		versionId: args.versionId,
		stichworte: args.stichworte,
		vonSekunde: args.vonSekunde,
		bisSekunde: args.bisSekunde,
	});
	if (error !== null) throw entwurfFehler(error);
	return data;
}

/* ---------- Das eigene Konto (Profil, 02.09.2026) ---------- */

export type KontoEinstellungen = { mailErinnerungen: boolean; mailTermine: boolean };

export const kontoEinstellungenQuery = queryOptions({
	queryKey: ["konto", "einstellungen"],
	queryFn: async (): Promise<{ kunde: boolean; einstellungen: KontoEinstellungen | null }> => {
		const { data, error } = await client.api.konto.einstellungen.get();
		if (error !== null) {
			throw new Error(
				`Die Einstellungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function kontoEinstellungenSpeichern(
	einstellungen: KontoEinstellungen,
): Promise<KontoEinstellungen> {
	const { data, error } = await client.api.konto.einstellungen.put(einstellungen);
	if (error !== null) throw entwurfFehler(error);
	return data.einstellungen;
}

/** Login-Adresse ändern — nur mit Passwort; die alte Adresse bekommt einen Hinweis. */
export async function kontoEmailAendern(args: {
	passwort: string;
	neueEmail: string;
}): Promise<{ email: string }> {
	const { data, error } = await client.api.konto.email.put(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Adresse konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/* ---------- Kalender-Abo (02.09.2026) ---------- */

export type KalenderStatus = { token: string; zuletztAbgerufen: string | null };

/** Das eigene Kalender-Abo — der Token wird beim ersten Aufruf angelegt. */
export const kontoKalenderQuery = queryOptions({
	queryKey: ["konto", "kalender"],
	queryFn: async (): Promise<KalenderStatus> => {
		const { data, error } = await client.api.konto.kalender.get();
		if (error !== null) {
			throw new Error(
				`Das Kalender-Abo konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

/** Neue Abo-Adresse — der alte Feed ist danach sofort tot (Rundgang 12.09.2026). */
export async function kalenderAdresseErneuern(): Promise<KalenderStatus> {
	const { data, error } = await client.api.konto.kalender.erneuern.post();
	if (error !== null) {
		throw new Error(`Die Adresse konnte nicht erneuert werden (Status ${String(error.status)}).`);
	}
	return data;
}

/** Die Abo-Adresse für Kalender-Programme (webcal öffnet direkt Apple/Outlook). */
export function kalenderFeedUrl(token: string): { https: string; webcal: string } {
	const https = `${window.location.origin}/api/kalender/${encodeURIComponent(token)}.ics`;
	return { https, webcal: https.replace(/^https?:\/\//, "webcal://") };
}

/* ---------- Module und Redaktionsplan (Etappe 6, 02.09.2026) ---------- */

export const modulWerte = ["social_media_betreuung", "studio", "kampagnen", "kalkulator"] as const;
export type Modul = (typeof modulWerte)[number];

export type KundenModulDto = {
	customerId: string;
	modul: Modul;
	aktiv: boolean;
	gebuchtVon: string | null;
	gebuchtAm: string;
	geaendertAm: string;
};

/** Die Module des eigenen Kunden — das Kundenmenü und die gesperrten Ansichten lesen hier. */
export const meineModuleQuery = queryOptions({
	queryKey: ["module", "mein"],
	queryFn: async (): Promise<{
		kunde: { id: string; name: string } | null;
		aktiv: Modul[];
		/** Offenes Interesse (04.09.2026): Der Bereich zeigt „Anfrage läuft". */
		interesse: Modul[];
	}> => {
		const { data, error } = await client.api.module.mein.get();
		if (error !== null) {
			throw new Error(`Die Module konnten nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

export async function modulInteresseMelden(args: {
	modul: Modul;
	nachricht?: string;
}): Promise<{ gemeldet: boolean }> {
	const { data, error } = await client.api.module.mein.interesse.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({ value: error.value, fallback: "Das hat gerade nicht geklappt." }),
		);
	}
	return data;
}

/** Wer hat welches Modul gebucht — für die Kundenliste des Teams. */
export const moduleGebuchtQuery = queryOptions({
	queryKey: ["module", "gebucht"],
	queryFn: async (): Promise<Record<Modul, string[]>> => {
		const { data, error } = await client.api.module.gebucht.get();
		if (error !== null) {
			throw new Error(`Die Module konnten nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

/** Wer hat wann Interesse an einem Modul gemeldet — die Kundenakte zeigt es (04.09.2026). */
export function kundenModulInteresseQuery(customerId: string) {
	return queryOptions({
		queryKey: ["module", "kunde", customerId, "interesse"],
		queryFn: async (): Promise<
			{ modul: Modul; nachricht: string | null; gemeldetAm: string }[]
		> => {
			const { data, error } = await client.api.module.kunden({ customerId }).interesse.get();
			if (error !== null) {
				throw new Error(
					`Das Interesse konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export function kundenModuleQuery(customerId: string) {
	return queryOptions({
		queryKey: ["module", "kunde", customerId],
		queryFn: async (): Promise<KundenModulDto[]> => {
			const { data, error } = await client.api.module.kunden({ customerId }).get();
			if (error !== null) {
				throw new Error(
					`Die Module konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function kundenModulSetzen(args: {
	customerId: string;
	modul: Modul;
	aktiv: boolean;
}): Promise<KundenModulDto> {
	const { data, error } = await client.api.module
		.kunden({ customerId: args.customerId })({ modul: args.modul })
		.put({ aktiv: args.aktiv });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({ value: error.value, fallback: "Das Modul ließ sich nicht umschalten." }),
		);
	}
	return data;
}

export const plattformWerte = ["instagram", "tiktok", "linkedin", "facebook", "youtube"] as const;
export type Plattform = (typeof plattformWerte)[number];

export const beitragStatusWerte = [
	"entwurf",
	"beim_kunden",
	"aenderung",
	"freigegeben",
	"geplant",
	"veroeffentlicht",
] as const;
export type BeitragStatus = (typeof beitragStatusWerte)[number];

export const formatWerte = ["feed", "reel", "story", "video", "short"] as const;
export type BeitragFormat = (typeof formatWerte)[number];

export type BeitragMediumDto = {
	id: string;
	dateiname: string;
	mimeTyp: string;
	groesse: number;
	reihenfolge: number;
	art: "bild" | "video";
};

export type BeitragKommentarDto = {
	id: string;
	vonRolle: "team" | "kunde";
	autorName: string;
	text: string;
	createdAt: string;
};

export type BeitragDto = {
	id: string;
	customerId: string;
	plattform: Plattform;
	titel: string | null;
	text: string;
	geplantFuer: string;
	status: BeitragStatus;
	format: BeitragFormat;
	veroeffentlichtAm: string | null;
	freigegebenAm: string | null;
	freigegebenVon: string | null;
	thema: string | null;
	kernbotschaft: string | null;
	begruendung: string | null;
	kiEntwurf: {
		hooks: string[];
		titel: string[];
		caption: string;
		hashtags: Record<string, string[]>;
	} | null;
	clipIds: string[];
	reichweite: number | null;
	interaktionen: number | null;
	followerEffekt: number | null;
	kennzahlenAm: string | null;
	medien: BeitragMediumDto[];
	kommentare: BeitragKommentarDto[];
	createdAt: string;
	updatedAt: string;
};

export type BeitragAnlegen = {
	plattform: Plattform;
	format?: BeitragFormat;
	titel?: string;
	text: string;
	geplantFuer: string;
};

export type BeitragBearbeiten = {
	plattform?: Plattform;
	format?: BeitragFormat;
	titel?: string | null;
	text?: string;
	geplantFuer?: string;
};

const REDAKTIONSPLAN_FEHLER = "Der Redaktionsplan konnte gerade nicht geladen werden.";

/** Die Beiträge des eigenen Kunden (Kundensicht) — 403 heißt: Modul nicht gebucht. */
export function meineBeitraegeQuery(zeitraum?: { von?: string; bis?: string }) {
	return queryOptions({
		queryKey: ["redaktionsplan", "mein", zeitraum?.von ?? "", zeitraum?.bis ?? ""],
		queryFn: async (): Promise<BeitragDto[]> => {
			const { data, error } = await client.api.redaktionsplan.mein.beitraege.get({
				query: zeitraum ?? {},
			});
			if (error !== null) {
				throw new Error(fehlermeldungVon({ value: error.value, fallback: REDAKTIONSPLAN_FEHLER }));
			}
			return data;
		},
	});
}

export function kundenBeitraegeQuery(
	customerId: string,
	zeitraum?: { von?: string; bis?: string },
) {
	return queryOptions({
		queryKey: ["redaktionsplan", "kunde", customerId, zeitraum?.von ?? "", zeitraum?.bis ?? ""],
		queryFn: async (): Promise<BeitragDto[]> => {
			const { data, error } = await client.api.redaktionsplan
				.kunden({ customerId })
				.beitraege.get({ query: zeitraum ?? {} });
			if (error !== null) {
				throw new Error(fehlermeldungVon({ value: error.value, fallback: REDAKTIONSPLAN_FEHLER }));
			}
			return data;
		},
	});
}

function beitragFehler(error: { value: unknown }, fallback: string): Error {
	return new Error(
		fehlermeldungVon({ value: error.value as string | number | object | null, fallback }),
	);
}

export async function beitragAnlegen(args: {
	customerId: string;
	daten: BeitragAnlegen;
}): Promise<BeitragDto> {
	const { data, error } = await client.api.redaktionsplan
		.kunden({ customerId: args.customerId })
		.beitraege.post(args.daten);
	if (error !== null) throw beitragFehler(error, "Der Beitrag konnte nicht angelegt werden.");
	return data;
}

export async function beitragBearbeiten(args: {
	id: string;
	daten: BeitragBearbeiten;
}): Promise<{ beitrag: BeitragDto; freigabeZurueckgesetzt: boolean }> {
	const { data, error } = await client.api.redaktionsplan
		.beitraege({ id: args.id })
		.patch(args.daten);
	if (error !== null) throw beitragFehler(error, "Der Beitrag konnte nicht gespeichert werden.");
	return data;
}

export async function beitragVerschieben(args: {
	id: string;
	geplantFuer: string;
}): Promise<{ beitrag: BeitragDto; freigabeZurueckgesetzt: boolean }> {
	const { data, error } = await client.api.redaktionsplan
		.beitraege({ id: args.id })
		.verschieben.post({ geplantFuer: args.geplantFuer });
	if (error !== null) throw beitragFehler(error, "Der Beitrag konnte nicht verschoben werden.");
	return data;
}

export async function beitragStatusSetzen(args: {
	id: string;
	status: BeitragStatus;
}): Promise<BeitragDto> {
	const { data, error } = await client.api.redaktionsplan
		.beitraege({ id: args.id })
		.status.post({ status: args.status });
	if (error !== null) throw beitragFehler(error, "Der Status ließ sich nicht setzen.");
	return data;
}

export async function beitragLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.redaktionsplan.beitraege({ id: args.id }).delete();
	if (error !== null) throw beitragFehler(error, "Der Beitrag konnte nicht gelöscht werden.");
}

export async function beitragKommentieren(args: {
	id: string;
	text: string;
	sicht: "team" | "kunde";
}): Promise<BeitragKommentarDto> {
	const { data, error } =
		args.sicht === "team"
			? await client.api.redaktionsplan
					.beitraege({ id: args.id })
					.kommentare.post({ text: args.text })
			: await client.api.redaktionsplan.mein
					.beitraege({ id: args.id })
					.kommentare.post({ text: args.text });
	if (error !== null) throw beitragFehler(error, "Der Kommentar konnte nicht gespeichert werden.");
	return data;
}

export async function beitragFreigeben(args: { id: string }): Promise<BeitragDto> {
	const { data, error } = await client.api.redaktionsplan.mein
		.beitraege({ id: args.id })
		.freigeben.post();
	if (error !== null) throw beitragFehler(error, "Die Freigabe hat nicht geklappt.");
	return data;
}

export async function beitragAenderungWuenschen(args: {
	id: string;
	text: string;
}): Promise<BeitragDto> {
	const { data, error } = await client.api.redaktionsplan.mein
		.beitraege({ id: args.id })
		.aenderung.post({ text: args.text });
	if (error !== null) throw beitragFehler(error, "Der Änderungswunsch kam nicht an.");
	return data;
}

export async function beitragMediumHochladen(args: {
	id: string;
	datei: File;
}): Promise<BeitragMediumDto> {
	const { data, error } = await client.api.redaktionsplan.beitraege({ id: args.id }).medien.post({
		dateiname: args.datei.name,
		mimeTyp: args.datei.type === "" ? "application/octet-stream" : args.datei.type,
		datenBase64: await dateiAlsBase64(args.datei),
	});
	if (error !== null) {
		throw beitragFehler(error, `„${args.datei.name}“ konnte nicht hochgeladen werden.`);
	}
	return data;
}

export async function beitragMediumLoeschen(args: { id: string; mediumId: string }): Promise<void> {
	const { error } = await client.api.redaktionsplan
		.beitraege({ id: args.id })
		.medien({ mediumId: args.mediumId })
		.delete();
	if (error !== null) throw beitragFehler(error, "Das Medium konnte nicht gelöscht werden.");
}

/** Die Adresse eines Mediums fürs <img>/<video> — je nach Tür. */
export function beitragMediumUrl(args: {
	beitragId: string;
	mediumId: string;
	sicht: "team" | "kunde";
}): string {
	return args.sicht === "team"
		? `/api/redaktionsplan/beitraege/${args.beitragId}/medien/${args.mediumId}`
		: `/api/redaktionsplan/mein/beitraege/${args.beitragId}/medien/${args.mediumId}`;
}

/** Sammel-Freigabe aus der Kundenliste — jeder Beitrag einzeln nach denselben Regeln. */
export async function beitragMehrereFreigeben(args: {
	ids: string[];
}): Promise<{ freigegeben: string[]; uebersprungen: string[] }> {
	const { data, error } = await client.api.redaktionsplan.mein.beitraege.freigeben.post({
		ids: args.ids,
	});
	if (error !== null) throw beitragFehler(error, "Die Freigabe hat nicht geklappt.");
	return data;
}

/* ---------- KI-Redaktionsplan (KI-REDAKTIONSPLAN-AUFTRAG.md, 03.09.2026) ---------- */

export type SocialPaket = {
	beitraegeProWoche: number;
	reels: number;
	fotos: number;
	plattformen: Plattform[];
	nische: string | null;
};

export const vorschlagArtWerte = [
	"beitrag",
	"beitrag-aenderung",
	"aufgabe-team",
	"aufgabe-kunde",
	"entscheidung",
	"idee",
	"zuordnung",
	"luecke",
	"ki-entwurf",
	"shotlist",
] as const;
export type VorschlagArt = (typeof vorschlagArtWerte)[number];
export type VorschlagStatus = "offen" | "bestaetigt" | "verworfen";

export type KiVorschlagDto = {
	id: string;
	customerId: string;
	art: VorschlagArt;
	bezugId: string | null;
	daten: Record<string, unknown>;
	begruendung: string | null;
	quelle: "vorplanung" | "transkript" | "material" | "radar" | "shotlist";
	status: VorschlagStatus;
	createdAt: string;
	entschiedenAm: string | null;
	entschiedenVon: string | null;
};

export type KundenIdeeDto = {
	id: string;
	customerId: string;
	titel: string;
	text: string;
	quelle: "call" | "transkript" | "manuell" | "radar";
	quelleBezug: string | null;
	status: "offen" | "verwendet" | "verworfen";
	createdAt: string;
};

export type KundenEntscheidungDto = {
	id: string;
	customerId: string;
	text: string;
	quelle: "transkript" | "manuell";
	quelleBezug: string | null;
	createdAt: string;
};

export type PlanungsAufgabeDto = {
	id: string;
	customerId: string;
	fuer: "team" | "kunde";
	titel: string;
	hinweis: string | null;
	faelligAm: string | null;
	erledigtAm: string | null;
	quelle: "transkript" | "manuell";
	createdAt: string;
};

export type PlanungsTranskriptDto = {
	id: string;
	customerId: string;
	titel: string;
	zeichen: number;
	quelle: "eingefuegt" | "aufnahme" | "meet";
	verarbeitetAm: string | null;
	createdAt: string;
};

export type ShotlistDto = {
	id: string;
	customerId: string;
	projektId: string | null;
	titel: string;
	inhalt: string;
	beitragIds: string[];
	status: "entwurf" | "freigegeben";
	createdAt: string;
};

export type BriefingDto = { id: string; customerId: string; inhalt: string; createdAt: string };

export type KiUebersicht = {
	paket: SocialPaket;
	offeneVorschlaege: number;
	ideen: number;
	transkripte: number;
	shotlists: number;
	letztesBriefing: string | null;
	offeneAufgabenTeam: number;
	offeneAufgabenKunde: number;
	entscheidungen: number;
	laeuft: { vorplanung: boolean; transkript: boolean; radar: boolean };
};

const KI_FEHLER = "Die KI-Werkstatt konnte gerade nicht geladen werden.";

function kiFehler(error: { value: unknown }, fallback: string): Error {
	return new Error(
		fehlermeldungVon({ value: error.value as string | number | object | null, fallback }),
	);
}

const kiKunde = (customerId: string) => client.api.redaktionsplan.kunden({ customerId }).ki;

export function kiUebersichtQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "uebersicht"],
		queryFn: async (): Promise<KiUebersicht> => {
			const { data, error } = await kiKunde(customerId).get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
		// Solange ein Lauf läuft, öfter nachschauen.
		refetchInterval: (q) => {
			const l = q.state.data?.laeuft;
			return l !== undefined && (l.vorplanung || l.transkript || l.radar) ? 5000 : false;
		},
	});
}

export function kiVorschlaegeQuery(customerId: string, status: VorschlagStatus = "offen") {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "vorschlaege", status],
		queryFn: async (): Promise<KiVorschlagDto[]> => {
			const { data, error } = await kiKunde(customerId).vorschlaege.get({ query: { status } });
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

export function kiIdeenQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "ideen"],
		queryFn: async (): Promise<KundenIdeeDto[]> => {
			const { data, error } = await kiKunde(customerId).ideen.get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

export function kiEntscheidungenQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "entscheidungen"],
		queryFn: async (): Promise<KundenEntscheidungDto[]> => {
			const { data, error } = await kiKunde(customerId).entscheidungen.get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

export function kiAufgabenQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "aufgaben"],
		queryFn: async (): Promise<PlanungsAufgabeDto[]> => {
			const { data, error } = await kiKunde(customerId).aufgaben.get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

export function kiTranskripteQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "transkripte"],
		queryFn: async (): Promise<PlanungsTranskriptDto[]> => {
			const { data, error } = await kiKunde(customerId).transkripte.get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

export function kiShotlistsQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "shotlists"],
		queryFn: async (): Promise<ShotlistDto[]> => {
			const { data, error } = await kiKunde(customerId).shotlists.get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

export function kiBriefingsQuery(customerId: string) {
	return queryOptions({
		queryKey: ["redaktionsplan", "ki", customerId, "briefings"],
		queryFn: async (): Promise<BriefingDto[]> => {
			const { data, error } = await kiKunde(customerId).briefings.get();
			if (error !== null) throw kiFehler(error, KI_FEHLER);
			return data;
		},
	});
}

/** Die bestätigten Kunden-Aufgaben (Baustein 2) — für den Hub des Kunden. */
export const meineKiAufgabenQuery = queryOptions({
	queryKey: ["redaktionsplan", "mein", "aufgaben"],
	queryFn: async (): Promise<PlanungsAufgabeDto[]> => {
		const { data, error } = await client.api.redaktionsplan.mein.aufgaben.get();
		if (error !== null) return [];
		return data;
	},
});

export async function kiPaketSetzen(args: { customerId: string; paket: SocialPaket }) {
	const { error } = await kiKunde(args.customerId).paket.put(args.paket);
	if (error !== null) throw kiFehler(error, "Das Paket ließ sich nicht speichern.");
}

export async function kiVorplanungStarten(args: { customerId: string }) {
	const { error } = await kiKunde(args.customerId).vorplanung.post();
	if (error !== null) throw kiFehler(error, "Die Vorplanung ließ sich nicht starten.");
}

export async function kiVorschlagEntscheiden(args: {
	id: string;
	aktion: "bestaetigen" | "verwerfen";
	daten?: Record<string, unknown>;
}): Promise<KiVorschlagDto> {
	const { data, error } = await client.api.redaktionsplan.ki
		.vorschlaege({ id: args.id })
		.post({ aktion: args.aktion, daten: args.daten });
	if (error !== null) throw kiFehler(error, "Die Entscheidung kam nicht an.");
	return data;
}

export async function kiTranskriptAnlegen(args: {
	customerId: string;
	titel: string;
	text: string;
}): Promise<PlanungsTranskriptDto> {
	const { data, error } = await kiKunde(args.customerId).transkripte.post({
		titel: args.titel,
		text: args.text,
	});
	if (error !== null) throw kiFehler(error, "Das Transkript ließ sich nicht speichern.");
	return data;
}

export async function kiTranskriptAusAufnahme(args: {
	customerId: string;
	titel: string;
	datei: File;
}): Promise<PlanungsTranskriptDto> {
	const { data, error } = await kiKunde(args.customerId).transkripte.aufnahme.post({
		titel: args.titel,
		mimeTyp: args.datei.type === "" ? "audio/mp4" : args.datei.type,
		audioBase64: await dateiAlsBase64(args.datei),
	});
	if (error !== null) throw kiFehler(error, "Die Aufnahme ließ sich nicht transkribieren.");
	return data;
}

export async function kiTranskriptVerarbeiten(args: { customerId: string; transkriptId: string }) {
	const { error } = await kiKunde(args.customerId)
		.transkripte({ id: args.transkriptId })
		.verarbeiten.post();
	if (error !== null) throw kiFehler(error, "Die Auswertung ließ sich nicht starten.");
}

export async function kiShotlistVorschlagen(args: { customerId: string }): Promise<KiVorschlagDto> {
	const { data, error } = await kiKunde(args.customerId).shotlist.post();
	if (error !== null) throw kiFehler(error, "Die Shotlist ließ sich nicht erstellen.");
	return data;
}

export async function kiShotlistFreigeben(args: { id: string }) {
	const { error } = await client.api.redaktionsplan.ki.shotlists({ id: args.id }).freigeben.post();
	if (error !== null) throw kiFehler(error, "Die Shotlist ließ sich nicht freigeben.");
}

export async function kiIdeeAnlegen(args: {
	customerId: string;
	titel: string;
	text: string;
}): Promise<KundenIdeeDto> {
	const { data, error } = await kiKunde(args.customerId).ideen.post({
		titel: args.titel,
		text: args.text,
	});
	if (error !== null) throw kiFehler(error, "Die Idee ließ sich nicht speichern.");
	return data;
}

export async function kiIdeeStatus(args: { id: string; status: KundenIdeeDto["status"] }) {
	const { error } = await client.api.redaktionsplan.ki
		.ideen({ id: args.id })
		.status.post({ status: args.status });
	if (error !== null) throw kiFehler(error, "Der Status ließ sich nicht setzen.");
}

export async function kiRadarStarten(args: { customerId: string }) {
	const { error } = await kiKunde(args.customerId).radar.post();
	if (error !== null) throw kiFehler(error, "Der Radar ließ sich nicht starten.");
}

export async function kiBriefingErstellen(args: { customerId: string }): Promise<BriefingDto> {
	const { data, error } = await kiKunde(args.customerId).briefing.post();
	if (error !== null) throw kiFehler(error, "Das Briefing ließ sich nicht erstellen.");
	return data;
}

export async function kiAufgabeAnlegen(args: {
	customerId: string;
	fuer: "team" | "kunde";
	titel: string;
	hinweis?: string;
	faelligAm?: string;
}): Promise<PlanungsAufgabeDto> {
	const { customerId, ...body } = args;
	const { data, error } = await kiKunde(customerId).aufgaben.post(body);
	if (error !== null) throw kiFehler(error, "Die Aufgabe ließ sich nicht anlegen.");
	return data;
}

export async function kiAufgabeErledigen(args: { id: string; erledigt: boolean }) {
	const { error } = await client.api.redaktionsplan.ki
		.aufgaben({ id: args.id })
		.erledigt.post({ erledigt: args.erledigt });
	if (error !== null) throw kiFehler(error, "Die Aufgabe ließ sich nicht ändern.");
}

export async function kiEntscheidungAnlegen(args: {
	customerId: string;
	text: string;
}): Promise<KundenEntscheidungDto> {
	const { data, error } = await kiKunde(args.customerId).entscheidungen.post({ text: args.text });
	if (error !== null) throw kiFehler(error, "Die Entscheidung ließ sich nicht speichern.");
	return data;
}

export async function kiEntscheidungLoeschen(args: { id: string }) {
	const { error } = await client.api.redaktionsplan.ki.entscheidungen({ id: args.id }).delete();
	if (error !== null) throw kiFehler(error, "Die Entscheidung ließ sich nicht löschen.");
}

export async function beitragKennzahlenSetzen(args: {
	id: string;
	reichweite: number | null;
	interaktionen: number | null;
	followerEffekt: number | null;
}) {
	const { id, ...body } = args;
	const { error } = await client.api.redaktionsplan.beitraege({ id }).kennzahlen.put(body);
	if (error !== null) throw kiFehler(error, "Die Kennzahlen ließen sich nicht speichern.");
}

/* ---------- Team-Chat (CHAT-AUFTRAG.md Stufe 1) ---------- */

export type KanalArt = "kanal" | "direkt";

export type ChatKanalDto = {
	id: string;
	art: KanalArt;
	name: string | null;
	thema: string | null;
	offen: boolean;
	mitgliederIds: string[];
	erstelltVon: string;
	createdAt: string;
	archiviertAm: string | null;
	ungelesen: number;
	letzteNachrichtAm: string | null;
};

export type ChatAnhangDto = {
	id: string;
	key: string;
	dateiname: string;
	mimeTyp: string;
	groesse: number;
	transkript: string | null;
};

export type ChatNachrichtDto = {
	id: string;
	kanalId: string;
	autorId: string;
	text: string;
	elternId: string | null;
	projektId: string | null;
	customerId: string | null;
	erwaehnungen: string[];
	warnung: "passwort" | null;
	bezugVorschlag: { projektId: string | null; customerId: string | null } | null;
	/** Olgens Aufgaben-Vorschlag (Mein Board Etappe 3): „Soll ich das in Beatrices Board eintragen?" */
	aufgabeVorschlag: {
		titel: string;
		fuerUserId: string;
		status: "offen" | "angenommen" | "verworfen";
		aufgabeId: string | null;
	} | null;
	bearbeitetAm: string | null;
	geloeschtAm: string | null;
	createdAt: string;
	anhaenge: ChatAnhangDto[];
	reaktionen: { emoji: string; userIds: string[] }[];
	antworten: number;
	letzteAntwortAm: string | null;
};

/** Der Autor der KI-Beiträge im Chat (Stufe 4). */
export const OLGEN_AUTOR_ID = "olgen";

export const chatKanaeleQuery = queryOptions({
	queryKey: ["chat", "kanaele"],
	queryFn: async (): Promise<ChatKanalDto[]> => {
		const { data, error } = await client.api.chat.kanaele.get();
		if (error !== null) {
			throw new Error(`Der Chat konnte nicht geladen werden (Status ${String(error.status)}).`);
		}
		return data;
	},
});

export function chatNachrichtenQuery(kanalId: string) {
	return queryOptions({
		queryKey: ["chat", "nachrichten", kanalId],
		queryFn: async (): Promise<{
			kanal: ChatKanalDto | Omit<ChatKanalDto, "ungelesen" | "letzteNachrichtAm">;
			nachrichten: ChatNachrichtDto[];
		}> => {
			const { data, error } = await client.api.chat
				.kanaele({ kanalId })
				.nachrichten.get({ query: {} });
			if (error !== null) {
				throw new Error(
					error.status === 404
						? "Diesen Raum gibt es nicht (mehr)."
						: `Die Nachrichten konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/** Ältere Nachrichten vor `vor` (Seite nach hinten). */
export async function chatNachrichtenDavor(args: {
	kanalId: string;
	vor: string;
}): Promise<ChatNachrichtDto[]> {
	const { data, error } = await client.api.chat
		.kanaele({ kanalId: args.kanalId })
		.nachrichten.get({ query: { vor: args.vor } });
	if (error !== null) {
		throw new Error(
			`Ältere Nachrichten konnten nicht geladen werden (Status ${String(error.status)}).`,
		);
	}
	return data.nachrichten;
}

export function chatThreadQuery(nachrichtId: string) {
	return queryOptions({
		queryKey: ["chat", "thread", nachrichtId],
		queryFn: async (): Promise<{ eltern: ChatNachrichtDto; antworten: ChatNachrichtDto[] }> => {
			const { data, error } = await client.api.chat.nachrichten({ id: nachrichtId }).thread.get();
			if (error !== null) {
				throw new Error(`Der Thread konnte nicht geladen werden (Status ${String(error.status)}).`);
			}
			return data;
		},
	});
}

export function chatProjektQuery(projektId: string) {
	return queryOptions({
		queryKey: ["chat", "projekt", projektId],
		queryFn: async (): Promise<ChatNachrichtDto[]> => {
			const { data, error } = await client.api.chat.projekte({ id: projektId }).get();
			if (error !== null) {
				throw new Error(
					`Der Projekt-Chat konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function chatSuche(text: string): Promise<ChatNachrichtDto[]> {
	const { data, error } = await client.api.chat.suche.get({ query: { q: text } });
	if (error !== null) {
		throw new Error(`Die Suche hat nicht geklappt (Status ${String(error.status)}).`);
	}
	return data;
}

export async function chatKanalAnlegen(args: {
	name: string;
	thema?: string | null;
	offen?: boolean;
	mitgliederIds?: string[];
}): Promise<ChatKanalDto | Omit<ChatKanalDto, "ungelesen" | "letzteNachrichtAm">> {
	const { data, error } = await client.api.chat.kanaele.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kanal konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatDirektOeffnen(args: {
	mitUserId: string;
}): Promise<Omit<ChatKanalDto, "ungelesen" | "letzteNachrichtAm">> {
	const { data, error } = await client.api.chat.direkt.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Chat konnte nicht geöffnet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export type ChatAnhangEingang = { dateiname: string; mimeTyp: string; datenBase64: string };

/** Eine Datei aus dem Auswahl-Dialog oder der Aufnahme wird zum Anhang. */
export async function chatAnhangAusDatei(datei: File): Promise<ChatAnhangEingang> {
	return {
		dateiname: datei.name,
		mimeTyp: datei.type || "application/octet-stream",
		datenBase64: await dateiAlsBase64(datei),
	};
}

export async function chatSenden(args: {
	kanalId: string;
	text: string;
	elternId?: string | null;
	projektId?: string | null;
	customerId?: string | null;
	anhaenge?: ChatAnhangEingang[];
}): Promise<ChatNachrichtDto> {
	const { kanalId, ...body } = args;
	const { data, error } = await client.api.chat.kanaele({ kanalId }).nachrichten.post(body);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Nachricht konnte nicht gesendet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatGelesen(args: { kanalId: string }): Promise<void> {
	await client.api.chat.kanaele({ kanalId: args.kanalId }).gelesen.post();
}

export async function chatNachrichtAendern(args: {
	id: string;
	text?: string;
	projektId?: string | null;
	customerId?: string | null;
}): Promise<ChatNachrichtDto> {
	const { id, ...body } = args;
	const { data, error } = await client.api.chat.nachrichten({ id }).patch(body);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Nachricht konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatNachrichtLoeschen(args: { id: string }): Promise<ChatNachrichtDto> {
	const { data, error } = await client.api.chat.nachrichten({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Nachricht konnte nicht gelöscht werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatReagieren(args: {
	id: string;
	emoji: string;
}): Promise<ChatNachrichtDto> {
	const { data, error } = await client.api.chat
		.nachrichten({ id: args.id })
		.reaktionen.post({ emoji: args.emoji });
	if (error !== null) {
		throw new Error(`Die Reaktion hat nicht geklappt (Status ${String(error.status)}).`);
	}
	return data;
}

export async function chatKanalArchivieren(args: {
	kanalId: string;
	archiviert: boolean;
}): Promise<void> {
	const ziel = client.api.chat.kanaele({ kanalId: args.kanalId });
	const { error } = args.archiviert
		? await ziel.archivieren.post()
		: await ziel.wiederherstellen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Der Link zu einem Anhang — die Route prüft, ob das Konto den Raum sehen darf. */
export function chatAnhangUrl(id: string): string {
	return `/api/chat/anhaenge/${encodeURIComponent(id)}`;
}

/* ---------- Kunden-Glocke und Hinweis-Einstellungen (CHAT-AUFTRAG.md Stufe 2) ---------- */

/** Die Glocke eines Kundenkontos: nur die persönlichen Einträge. */
export const meinBenachrichtigungenQuery = queryOptions({
	queryKey: ["benachrichtigungen", "mein"],
	queryFn: async (): Promise<{ eintraege: BenachrichtigungDto[]; ungelesen: number }> => {
		const { data, error } = await client.api.mein.benachrichtigungen.get();
		if (error !== null) {
			throw new Error(
				`Die Benachrichtigungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	refetchInterval: 60_000,
});

export async function meinAlleGelesen(): Promise<void> {
	await client.api.mein.benachrichtigungen.gelesen.post();
}

export type ChatUmfang = "erwaehnungen" | "alle";

export type KontoHinweise = {
	ton: boolean;
	popup: boolean;
	push: boolean;
	chatUmfang: ChatUmfang;
};

export const kontoHinweiseQuery = queryOptions({
	queryKey: ["konto", "hinweise"],
	queryFn: async (): Promise<KontoHinweise> => {
		const { data, error } = await client.api.konto.hinweise.get();
		if (error !== null) {
			throw new Error(
				`Die Hinweis-Einstellungen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	staleTime: 5 * 60_000,
});

export async function kontoHinweiseSpeichern(hinweise: KontoHinweise): Promise<KontoHinweise> {
	const { data, error } = await client.api.konto.hinweise.put(hinweise);
	if (error !== null) {
		throw new Error(
			`Die Einstellungen konnten nicht gespeichert werden (Status ${String(error.status)}).`,
		);
	}
	return data;
}

/* ---------- Der Einrichtungs-Guide (13.09.2026) ---------- */

export type EinrichtungSchritt =
	| "einfuehrung"
	| "profil"
	| "benachrichtigungen"
	| "kalender"
	| "unterlagen";
export type EinrichtungStatus = "offen" | "erledigt" | "uebersprungen";
export type EinrichtungStand = {
	schritte: {
		key: EinrichtungSchritt;
		titel: string;
		kurz: string;
		ueberspringbar: boolean;
		status: EinrichtungStatus;
		am: string | null;
	}[];
	fertig: boolean;
	naechster: EinrichtungSchritt | null;
};

export const kontoEinrichtungQuery = queryOptions({
	queryKey: ["konto", "einrichtung"],
	queryFn: async (): Promise<EinrichtungStand> => {
		const { data, error } = await client.api.konto.einrichtung.get();
		if (error !== null) {
			throw new Error(
				`Der Einrichtungs-Stand konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	staleTime: 60_000,
});

export async function einrichtungSchrittSetzen(args: {
	schritt: EinrichtungSchritt;
	status: EinrichtungStatus;
}): Promise<EinrichtungStand> {
	const { data, error } = await client.api.konto
		.einrichtung({ schritt: args.schritt })
		.put({ status: args.status });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Schritt konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/* ---------- Web Push (CHAT-AUFTRAG.md Stufe 3) ---------- */

export type PushAboDto = {
	id: string;
	userId: string;
	endpoint: string;
	p256dh: string;
	auth: string;
	userAgent: string | null;
	createdAt: string;
	zuletztGesendet: string | null;
};

/** Öffentlicher Schlüssel (null = Push auf dem System aus) und die eigenen Abos. */
export const kontoPushQuery = queryOptions({
	queryKey: ["konto", "push"],
	queryFn: async (): Promise<{
		publicKey: string | null;
		eingerichtet: boolean;
		abos: PushAboDto[];
	}> => {
		const { data, error } = await client.api.konto.push.get();
		if (error !== null) {
			throw new Error(
				`Der Push-Stand konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	staleTime: 60_000,
});

export async function pushAboAnlegen(args: {
	endpoint: string;
	p256dh: string;
	auth: string;
	userAgent?: string | null;
}): Promise<PushAboDto> {
	const { data, error } = await client.api.konto.push.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Push konnte nicht aktiviert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function pushAboLoeschen(args: { endpoint: string }): Promise<void> {
	const { error } = await client.api.konto.push.delete({ endpoint: args.endpoint });
	if (error !== null) {
		throw new Error(`Push konnte nicht abgeschaltet werden (Status ${String(error.status)}).`);
	}
}

/* ---------- KI im Chat (CHAT-AUFTRAG.md Stufe 4) ---------- */

export type OffenerPunktDto = { nachrichtId: string; von: string; punkt: string; aufgabe: string };
export type VerpasstDto = {
	anzahl: number;
	seit: string | null;
	zusammenfassung: string;
	nachrichtIds: string[];
};
export type ProjektNotizDto = {
	id: string;
	projektId: string;
	text: string;
	quelle: string;
	nachrichtId: string | null;
	erstelltVon: string;
	createdAt: string;
};

export async function chatVerpasst(args: { kanalId: string }): Promise<VerpasstDto> {
	const { data, error } = await client.api.chat
		.kanaele({ kanalId: args.kanalId })
		.ki.verpasst.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Zusammenfassung hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatOffenePunkte(args: { kanalId: string }): Promise<OffenerPunktDto[]> {
	const { data, error } = await client.api.chat
		.kanaele({ kanalId: args.kanalId })
		.ki["offene-punkte"].post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die offenen Punkte konnten nicht ermittelt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data.punkte;
}

export async function chatKiAufgabe(args: {
	nachrichtId: string;
	projektId: string;
	titel: string;
	beschreibung?: string | null;
}): Promise<void> {
	const { error } = await client.api.chat.ki.aufgabe.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Aufgabe konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function chatThreadZusammenfassen(args: {
	nachrichtId: string;
}): Promise<ChatNachrichtDto> {
	const { data, error } = await client.api.chat
		.nachrichten({ id: args.nachrichtId })
		.ki.zusammenfassung.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Zusammenfassung hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatNotizAnheften(args: {
	nachrichtId: string;
	projektId: string;
	text: string;
}): Promise<ProjektNotizDto> {
	const { nachrichtId, ...body } = args;
	const { data, error } = await client.api.chat
		.nachrichten({ id: nachrichtId })
		.ki.notiz.post(body);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Notiz konnte nicht angeheftet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export function chatProjektNotizenQuery(projektId: string) {
	return queryOptions({
		queryKey: ["chat", "notizen", projektId],
		queryFn: async (): Promise<ProjektNotizDto[]> => {
			const { data, error } = await client.api.chat.projekte({ id: projektId }).notizen.get();
			if (error !== null) {
				throw new Error(
					`Die Notizen konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/** Den KI-Vorschlag für den Bezug wegklicken. */
/** Olgens Aufgaben-Vorschlag annehmen — die Karte landet auf dem Board der Person. */
export async function chatAufgabeVorschlagAnnehmen(args: {
	id: string;
}): Promise<{ aufgabeId: string }> {
	const { data, error } = await client.api.chat
		.nachrichten({ id: args.id })
		.ki["aufgabe-vorschlag"].annehmen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function chatAufgabeVorschlagVerwerfen(args: { id: string }): Promise<void> {
	const { error } = await client.api.chat
		.nachrichten({ id: args.id })
		.ki["aufgabe-vorschlag"].verwerfen.post();
	if (error !== null) {
		throw new Error(`Das hat nicht geklappt (Status ${String(error.status)}).`);
	}
}

export async function chatVorschlagVerwerfen(args: { id: string }): Promise<ChatNachrichtDto> {
	const { data, error } = await client.api.chat
		.nachrichten({ id: args.id })
		.patch({ vorschlagVerwerfen: true });
	if (error !== null) {
		throw new Error(`Das hat nicht geklappt (Status ${String(error.status)}).`);
	}
	return data;
}

/** Geschlossene Kanäle (03.09.2026): Mitglied dazu oder raus. */
export async function chatMitgliedAendern(args: {
	kanalId: string;
	userId: string;
	aktion: "hinzu" | "raus";
}): Promise<void> {
	const ziel = client.api.chat.kanaele({ kanalId: args.kanalId });
	const { error } =
		args.aktion === "hinzu"
			? await ziel.mitglieder.post({ userId: args.userId })
			: await ziel.mitglieder({ userId: args.userId }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Mitgliederliste konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

// ── Transkripte der Inhalte (Baustein 4, sichtbar seit dem 03.09.2026) ──

export type InhaltTranskriptDto = {
	id: string;
	dateiId: string;
	inhaltId: string;
	text: string;
	status: "laeuft" | "fertig" | "fehler" | "zu_gross";
	fehler: string | null;
	createdAt: string;
};

/** Die Transkripte aller Dateien eines Inhalts — läuft im Hintergrund, deshalb sanft pollen, solange etwas läuft. */
export function inhaltTranskripteQuery(inhaltId: string) {
	return queryOptions({
		queryKey: ["inhalt-transkripte", inhaltId],
		queryFn: async (): Promise<InhaltTranskriptDto[]> => {
			const { data, error } = await client.api.redaktionsplan.ki
				.inhalte({ id: inhaltId })
				.transkripte.get();
			if (error !== null) {
				throw new Error(
					`Die Transkripte konnten nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
		refetchInterval: (query) =>
			query.state.data?.some((t) => t.status === "laeuft") === true ? 10_000 : false,
	});
}

/** Transkription für alle Dateien der aktuellen Version (neu) anstoßen — für Videos aus der Zeit vor der Automatik oder nach einem Fehler. */
export async function inhaltTranskribieren(inhaltId: string): Promise<{ eingeplant: number }> {
	const { data, error } = await client.api.redaktionsplan.ki
		.inhalte({ id: inhaltId })
		.transkribieren.post();
	if (error !== null) {
		throw new Error(
			`Die Transkription konnte nicht gestartet werden (Status ${String(error.status)}).`,
		);
	}
	return data;
}

/** Kanal öffnen (ganzes Team) oder schließen (Mitgliederliste) — Anleger oder Inhaber. */
export async function chatKanalOffenSetzen(args: {
	kanalId: string;
	offen: boolean;
}): Promise<void> {
	const { error } = await client.api.chat
		.kanaele({ kanalId: args.kanalId })
		.patch({ offen: args.offen });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kanal konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Kanal umbenennen oder das Thema ändern (Anleger oder Inhaber). */
export async function chatKanalBearbeiten(args: {
	kanalId: string;
	name?: string;
	thema?: string | null;
}): Promise<void> {
	const { error } = await client.api.chat
		.kanaele({ kanalId: args.kanalId })
		.daten.patch({ name: args.name, thema: args.thema });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kanal konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/* ---------- Erklärvideos je Stelle im Kundenbereich (10.09.2026) ---------- */

export const erklaervideoStellen = ["einfuehrung", "feedback", "redaktionsplan"] as const;
export type ErklaervideoStelle = (typeof erklaervideoStellen)[number];

export type ErklaervideoEinbettung =
	| { art: "iframe"; src: string; anbieter: "loom" | "youtube" | "vimeo" }
	| { art: "datei"; src: string };

export type ErklaervideoDto = {
	stelle: ErklaervideoStelle;
	titel: string;
	url: string;
	einbettung: ErklaervideoEinbettung;
	updatedAt: string;
};

export type ErklaervideoStellenMeta = Record<
	ErklaervideoStelle,
	{ label: string; wo: string; vorschlag: string }
>;

export const erklaervideosQuery = queryOptions({
	queryKey: ["erklaervideos"],
	queryFn: async (): Promise<{ videos: ErklaervideoDto[]; stellen: ErklaervideoStellenMeta }> => {
		const { data, error } = await client.api.erklaervideos.get();
		if (error !== null) {
			throw new Error(
				`Die Erklärvideos konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	// Fünf Minuten: Ein frisch eingetragenes Video soll beim Kunden nicht eine Stunde auf sich warten lassen.
	staleTime: 5 * 60 * 1000,
});

export async function erklaervideoSpeichern(args: {
	stelle: ErklaervideoStelle;
	url: string;
	titel?: string;
}): Promise<ErklaervideoDto> {
	const { data, error } = await client.api
		.erklaervideos({ stelle: args.stelle })
		.put({ url: args.url, ...(args.titel !== undefined ? { titel: args.titel } : {}) });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Video konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function erklaervideoLoeschen(args: { stelle: ErklaervideoStelle }): Promise<void> {
	const { error } = await client.api.erklaervideos({ stelle: args.stelle }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Video konnte nicht entfernt werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

// ─── Anfragen: Interessenten, Erstgespräch, Angebote, Baukasten (11.09.2026) ──

export type {
	AngebotArchivZeile,
	AngebotDto,
	AngebotEreignisDto,
	AngebotInhalt,
	AngebotKontingent,
	AngebotKundenSicht,
	AngebotOeffentlich,
	AngebotsVorlageDto,
	BausteinDto,
	GespraechDto,
	InteressentAkte,
	InteressentDto,
	InteressentZeile,
	KontingentFormat as BackendKontingentFormat,
	KontingentReisekosten as BackendKontingentReisekosten,
	KontingentZahlung as BackendKontingentZahlung,
	KundenVerlauf,
	NummernStand,
	Protokoll,
} from "@olgen/api";

export const kanalWerte = [
	"email",
	"whatsapp",
	"telefon",
	"website",
	"empfehlung",
	"ads",
	"vor_ort",
	"kundenbereich",
	"sonstiges",
] as const;
export type Kanal = (typeof kanalWerte)[number];

export const interessentStatusWerte = [
	"neu",
	"gespraech_geplant",
	"gespraech_gefuehrt",
	"angebot_in_arbeit",
	"angebot_gesendet",
	"gewonnen",
	"verloren",
	"pausiert",
] as const;
export type InteressentStatus = (typeof interessentStatusWerte)[number];

export const angebotsartWerte = ["projekt", "betreuung"] as const;
export type Angebotsart = (typeof angebotsartWerte)[number];

export const angebotStatusWerte = [
	"entwurf",
	"freigegeben",
	"gesendet",
	"angesehen",
	"unterschrieben",
	"abgelehnt",
	"abgelaufen",
	"storniert",
] as const;
export type AngebotStatus = (typeof angebotStatusWerte)[number];

/** Anrede im Angebot (13.09.2026): Du ist der Standard, Sie auf Wunsch des Interessenten. */
export const anredeWerte = ["du", "sie"] as const;
export type Anrede = (typeof anredeWerte)[number];

/** Der Testimonial-Baukasten am Kontingent (14.09.2026) — Listen wie im Backend (`kontingent.text.ts`). */
export const kontingentFormatWerte = ["quer", "hoch", "quadrat"] as const;
export type KontingentFormat = (typeof kontingentFormatWerte)[number];
export const kontingentReisekostenWerte = ["inklusive", "separat"] as const;
export type KontingentReisekosten = (typeof kontingentReisekostenWerte)[number];
export const kontingentZahlungWerte = ["je_produktion", "raten", "einmalig"] as const;
export type KontingentZahlung = (typeof kontingentZahlungWerte)[number];

export const bausteinArtWerte = [
	"modul",
	"garantie",
	"bedingungen",
	"naechste_schritte",
	"leistungsabgrenzung",
	"ablauf",
	"laufzeit",
] as const;
export type BausteinArt = (typeof bausteinArtWerte)[number];
export type BausteinAngebotsart = "projekt" | "betreuung" | "beide";

/** Was das Formular liefert — überall Strings, leere Felder sind "". */
export type InteressentFormularDaten = {
	firma: string;
	ansprechpartner: string;
	email: string;
	telefon: string;
	website: string;
	adresse: string;
	kanal: Kanal;
	ersteNachricht: string;
	notizen: string;
	erstgespraechAm: string;
	/** Früherer Kunde, der sich wieder meldet — "" = keiner (nur beim Anlegen). */
	customerId: string;
};

function interessentBody(daten: InteressentFormularDaten) {
	return {
		firma: daten.firma.trim(),
		ansprechpartner: zuApiFeld(daten.ansprechpartner),
		email: zuApiFeld(daten.email),
		telefon: zuApiFeld(daten.telefon),
		website: zuApiFeld(daten.website),
		adresse: zuApiFeld(daten.adresse),
		kanal: daten.kanal,
		ersteNachricht: zuApiFeld(daten.ersteNachricht),
		notizen: zuApiFeld(daten.notizen),
		erstgespraechAm: zuApiFeld(daten.erstgespraechAm),
		...(daten.customerId === "" ? {} : { customerId: daten.customerId }),
	};
}

export const anfragenQuery = queryOptions({
	queryKey: ["anfragen"],
	queryFn: async (): Promise<InteressentZeile[]> => {
		const { data, error } = await client.api.anfragen.get({ query: {} });
		if (error !== null) {
			throw new Error(
				`Die Anfragen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export const anfragenArchivQuery = queryOptions({
	queryKey: ["anfragen", "archiv"],
	queryFn: async (): Promise<InteressentZeile[]> => {
		const { data, error } = await client.api.anfragen.get({ query: { archiv: "1" } });
		if (error !== null) {
			throw new Error(
				`Die Anfragen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data.filter((i) => i.archiviertAm !== null);
	},
});

export function anfrageAkteQuery(id: string) {
	return queryOptions({
		queryKey: ["anfragen", id],
		queryFn: async (): Promise<InteressentAkte> => {
			const { data, error } = await client.api.anfragen({ id }).get();
			if (error !== null) {
				throw new Error(
					fehlermeldungVon({
						value: error.value,
						fallback: `Die Anfrage konnte nicht geladen werden (Status ${String(error.status)}).`,
					}),
				);
			}
			return data;
		},
		// Solange die KI arbeitet, alle 4 s nachsehen — danach Ruhe.
		refetchInterval: (query) => {
			const l = query.state.data?.laeuft;
			return l !== undefined && (l.zusammenfassung || l.protokoll || l.entwuerfe.length > 0)
				? 4_000
				: false;
		},
	});
}

export async function interessentAnlegen(daten: InteressentFormularDaten): Promise<InteressentDto> {
	const { data, error } = await client.api.anfragen.post(interessentBody(daten));
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Interessent konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Der Verlauf eines Kunden: Anfragen mit Gesprächen und Angeboten (Kundenansicht, 23.09.2026). */
export function kundenVerlaufQuery(customerId: string) {
	return queryOptions({
		queryKey: ["anfragen", "kunde", customerId, "verlauf"],
		queryFn: async (): Promise<KundenVerlauf> => {
			const { data, error } = await client.api.anfragen.kunde({ customerId }).verlauf.get();
			if (error !== null) {
				throw new Error(
					`Der Verlauf konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}
export async function interessentAendern(args: {
	id: string;
	daten: InteressentFormularDaten;
}): Promise<InteressentDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.put(interessentBody(args.daten));
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Änderung konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function interessentStatusSetzen(args: {
	id: string;
	status: InteressentStatus;
}): Promise<InteressentDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.status.post({ status: args.status });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Status konnte nicht gesetzt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function interessentArchiv(args: {
	id: string;
	aktion: "archivieren" | "wiederherstellen";
}): Promise<InteressentDto> {
	const pfad = client.api.anfragen({ id: args.id });
	const { data, error } =
		args.aktion === "archivieren"
			? await pfad.archivieren.post()
			: await pfad.wiederherstellen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function gespraechSpeichern(args: {
	id: string;
	daten: {
		gespraechId?: string | null;
		transkript?: string | null;
		quelle?: "aufnahme" | "upload" | "eingefuegt" | null;
		abgehakt?: string[];
		notiert?: Record<string, string>;
	};
}): Promise<GespraechDto> {
	const { data, error } = await client.api.anfragen({ id: args.id }).gespraech.put(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Gesprächsstand konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function protokollStarten(args: {
	id: string;
	gespraechId?: string | null;
	fragen: { id: string; frage: string }[];
}): Promise<void> {
	const { error } = await client.api
		.anfragen({ id: args.id })
		.gespraech.protokoll.post({ fragen: args.fragen, gespraechId: args.gespraechId ?? null });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Protokoll konnte nicht gestartet werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Ein weiteres Gespräch zur Anfrage — Folgegespräch (23.09.2026). */
export async function gespraechAnlegen(args: {
	id: string;
	art: "erstgespraech" | "folgegespraech";
}): Promise<GespraechDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.gespraeche.post({ art: args.art });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Gespräch konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Das Ergebnis eines Gesprächs in alle offenen Kalkulationen der Anfrage übernehmen (KI). */
export async function kalkAusGespraech(args: {
	gespraechId: string;
}): Promise<{ angepasst: number; titel: string[] }> {
	const { data, error } = await client.api.kalkulation["aus-gespraech"].post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Gespräch konnte nicht übernommen werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}
export async function angebotEntwerfen(args: {
	id: string;
	angebotsart?: Angebotsart;
	variante?: string | null;
	hinweis?: string | null;
	/** Die Vorlage als Skelett — ohne Angabe wählt der Server nach Bedarfsprofil. */
	vorlageId?: string | null;
}): Promise<void> {
	const { error } = await client.api.anfragen({ id: args.id }).angebote.entwerfen.post({
		...(args.angebotsart === undefined ? {} : { angebotsart: args.angebotsart }),
		variante: args.variante ?? null,
		hinweis: args.hinweis ?? null,
		vorlageId: args.vorlageId ?? null,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Entwurf konnte nicht gestartet werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function angebotAendern(args: {
	id: string;
	angebotId: string;
	daten: {
		titel?: string;
		variante?: string | null;
		angebotsart?: Angebotsart;
		inhalt?: AngebotInhalt;
		kiHinweise?: string | null;
		anrede?: Anrede;
	};
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.put(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Angebot konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotFreigeben(args: {
	id: string;
	angebotId: string;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.freigeben.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Freigabe hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotLoeschen(args: { id: string; angebotId: string }): Promise<void> {
	const { error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Angebot konnte nicht gelöscht werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Interessent endgültig löschen (13.09.2026) — nur ohne verschicktes Angebot, sonst 422. */
export async function interessentLoeschen(id: string): Promise<void> {
	const { error } = await client.api.anfragen({ id }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Interessent konnte nicht gelöscht werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

/** Anrede Du/Sie setzen — mit `umschreiben` formuliert die KI alle Texte um (13.09.2026). */
export async function angebotAnredeSetzen(args: {
	id: string;
	angebotId: string;
	anrede: Anrede;
	umschreiben: boolean;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.anrede.post({ anrede: args.anrede, umschreiben: args.umschreiben });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Anrede konnte nicht geändert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Vorschau wie beim Kunden (13.09.2026): dieselben Daten wie die Angebotsseite, über die Angebots-Id. */
export function angebotVorschauQuery(id: string, angebotId: string) {
	return queryOptions({
		queryKey: ["anfragen", id, "angebote", angebotId, "vorschau"],
		queryFn: async () => {
			const { data, error } = await client.api
				.anfragen({ id })
				.angebote({ angebotId })
				.vorschau.get();
			if (error !== null) {
				throw new Error(
					error.status === 404
						? "Dieses Angebot gibt es nicht (mehr)."
						: `Die Vorschau konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/** Monatliche Betreuung (13.09.2026): Abrechnung stoppen oder fortsetzen. */
export async function angebotAbrechnungSetzen(args: {
	id: string;
	angebotId: string;
	aktiv: boolean;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.abrechnung.put({ aktiv: args.aktiv });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Abrechnung konnte nicht umgestellt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function gekauftVorschlag(id: string): Promise<string> {
	const { data, error } = await client.api.anfragen({ id })["gekauft-vorschlag"].get();
	if (error !== null) return "";
	return data.purchased;
}

export async function interessentGewonnen(args: {
	id: string;
	name: string;
	purchased: string;
	/** Rundgang 12.09.2026: den Zugang zum Kundenbereich gleich mit anlegen. */
	zugangAnlegen: boolean;
	zugangEmail: string | null;
}): Promise<{
	customerId: string;
	projektId: string | null;
	zugang: { ok: boolean; mailGesendet: boolean } | null;
}> {
	const { data, error } = await client.api.anfragen({ id: args.id }).gewonnen.post({
		name: args.name,
		purchased: args.purchased,
		zugangAnlegen: args.zugangAnlegen,
		zugangEmail: args.zugangEmail,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Kunde konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return { customerId: data.customerId, projektId: data.projektId, zugang: data.zugang };
}

/** Die Erstgespräche mit Interessenten als Kalender-Einträge (Rundgang 12.09.2026) — nur lesend. */
export const erstgespraecheQuery = queryOptions({
	queryKey: ["anfragen", "erstgespraeche"],
	queryFn: async (): Promise<TerminDto[]> => {
		const { data, error } = await client.api.anfragen.erstgespraeche.get();
		if (error !== null)
			throw new Error(`Die Erstgespräche laden nicht (Status ${String(error.status)}).`);
		return data;
	},
});

/** „Euer Auftrag" (Rundgang 12.09.2026): die Angebote des eigenen Kunden — verschickt oder unterschrieben. */
export const meineAngeboteQuery = queryOptions({
	queryKey: ["mein", "angebote"],
	queryFn: async (): Promise<AngebotKundenSicht[]> => {
		const { data, error } = await client.api.mein.angebote.get();
		if (error !== null)
			throw new Error(`Die Angebote laden nicht (Status ${String(error.status)}).`);
		return data;
	},
});

export function meinAngebotPdfUrl(angebotId: string): string {
	return `/api/mein/angebote/${encodeURIComponent(angebotId)}/pdf`;
}

export const angebotsBausteineQuery = queryOptions({
	queryKey: ["angebots-bausteine"],
	queryFn: async (): Promise<BausteinDto[]> => {
		const { data, error } = await client.api["angebots-bausteine"].get();
		if (error !== null) {
			throw new Error(
				`Der Baukasten konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export type BausteinFormularDaten = {
	angebotsart: BausteinAngebotsart;
	art: BausteinArt;
	titel: string;
	ziel: string;
	inhalt: string;
	punkte: string;
	reihenfolge: number;
	aktiv: boolean;
};

export async function bausteinSpeichern(args: {
	id: string;
	daten: BausteinFormularDaten;
}): Promise<BausteinDto> {
	const punkte = Number.parseInt(args.daten.punkte, 10);
	const { data, error } = await client.api["angebots-bausteine"]({ id: args.id }).put({
		angebotsart: args.daten.angebotsart,
		art: args.daten.art,
		titel: args.daten.titel.trim(),
		ziel: zuApiFeld(args.daten.ziel),
		inhalt: args.daten.inhalt.trim(),
		punkte: Number.isFinite(punkte) ? punkte : null,
		reihenfolge: args.daten.reihenfolge,
		aktiv: args.daten.aktiv,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Baustein konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function bausteinLoeschen(id: string): Promise<void> {
	const { error } = await client.api["angebots-bausteine"]({ id }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Baustein konnte nicht gelöscht werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function bausteineZuruecksetzen(): Promise<BausteinDto[]> {
	const { data, error } = await client.api["angebots-bausteine"].zuruecksetzen.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Zurücksetzen hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

// ─── Leitfaden-Anpassungen (11.09.2026): Fragen je Abschnitt, team-weit ──────

export type LeitfadenKatalog = "erstgespraech" | "onboarding";

export function leitfadenQuery(katalog: LeitfadenKatalog) {
	return queryOptions({
		queryKey: ["leitfaden", katalog],
		queryFn: async (): Promise<Record<string, { fragen: OnboardingFrageDto[] }>> => {
			const { data, error } = await client.api.leitfaden({ katalog }).get();
			if (error !== null) {
				throw new Error(
					`Der Leitfaden konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
		staleTime: 5 * 60_000,
	});
}

/** Dieselbe Form wie `OnboardingFrage` in `lib/onboarding.ts`. */
export type OnboardingFrageDto = {
	id: string;
	text: string;
	hint?: string;
	optional?: boolean;
	art: "text" | "lang" | "auswahl" | "mehrfach" | "datum" | "info";
	optionen?: string[];
	notiz?: string;
};

export async function leitfadenSpeichern(args: {
	katalog: LeitfadenKatalog;
	abschnittId: string;
	fragen: OnboardingFrageDto[];
}): Promise<void> {
	const { error } = await client.api
		.leitfaden({ katalog: args.katalog })({ abschnittId: args.abschnittId })
		.put({ fragen: args.fragen });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Abschnitt konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function leitfadenVerwerfen(args: {
	katalog: LeitfadenKatalog;
	abschnittId: string;
}): Promise<void> {
	const { error } = await client.api
		.leitfaden({ katalog: args.katalog })({ abschnittId: args.abschnittId })
		.delete();
	if (error !== null && error.status !== 404) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Zurücksetzen hat nicht geklappt (Status ${String(error.status)}).`,
			}),
		);
	}
}

// ─── Das Angebotstool (12.09.2026): Vorlagen, Versand, Kundenseite ──────────

export const angebotEreignisArtWerte = [
	"erstellt",
	"freigegeben",
	"gesendet",
	"geoeffnet",
	"team_geoeffnet",
	"pdf_geladen",
	"unterschrieben",
	"abgelehnt",
	"erinnert",
	"storniert",
] as const;
export type AngebotEreignisArt = (typeof angebotEreignisArtWerte)[number];

export const vorlageTagWerte = [
	"imagefilm",
	"recruitingfilm",
	"kundenstimme",
	"mitarbeiterinterviews",
	"social-media-betreuung",
	"fotoshooting",
	"youtube-betreuung",
	"werbeanzeigen",
	"event",
	"drehtag",
] as const;
export type VorlageTag = (typeof vorlageTagWerte)[number];

export type VorlageDaten = {
	name: string;
	beschreibung: string | null;
	/** Gruppe in der Auswahl (15.09.2026): „Testimonials", „Imagefilm" … null = „Weitere". */
	gruppe: string | null;
	angebotsart: Angebotsart;
	dienstleistungen: string[];
	titel: string;
	inhalt: AngebotInhalt;
	reihenfolge: number;
	aktiv: boolean;
};

export const angebotsVorlagenQuery = queryOptions({
	queryKey: ["angebots-vorlagen"],
	queryFn: async () => {
		const { data, error } = await client.api["angebots-vorlagen"].get();
		if (error !== null) {
			throw new Error(
				`Die Angebotsvorlagen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function vorlageSpeichern(args: {
	id: string;
	daten: VorlageDaten;
}): Promise<AngebotsVorlageDto> {
	const { data, error } = await client.api["angebots-vorlagen"]({ id: args.id }).put(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Vorlage konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function vorlageLoeschen(id: string): Promise<void> {
	const { error } = await client.api["angebots-vorlagen"]({ id }).delete();
	if (error !== null && error.status !== 404) {
		throw new Error(`Die Vorlage konnte nicht gelöscht werden (Status ${String(error.status)}).`);
	}
}

export async function vorlagenZuruecksetzen(): Promise<AngebotsVorlageDto[]> {
	const { data, error } = await client.api["angebots-vorlagen"].zuruecksetzen.post();
	if (error !== null) {
		throw new Error(`Zurücksetzen hat nicht geklappt (Status ${String(error.status)}).`);
	}
	return data;
}

export async function angebotAusVorlage(args: {
	id: string;
	vorlageId: string;
	variante: string | null;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote["aus-vorlage"].post({ vorlageId: args.vorlageId, variante: args.variante });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Angebot konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotAlsVorlage(args: {
	id: string;
	angebotId: string;
	name: string;
}): Promise<AngebotsVorlageDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		["als-vorlage"].post({ name: args.name });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Vorlage konnte nicht gesichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotSenden(args: {
	id: string;
	angebotId: string;
	daten: { email: string; name: string; nachricht: string | null; kopieAnMich: boolean };
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.senden.post(args.daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Angebot konnte nicht verschickt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

// ── Das Regelwerk (12.09.2026): Storno, Neue Fassung, Archiv, Nummern, Schnellweg ──

/** Mehrere Angebote (13.09.2026): aus einem unterschriebenen Angebot das Projekt anlegen. */
export async function angebotProjektAnlegen(args: {
	id: string;
	angebotId: string;
}): Promise<{ projektId: string | null; angebot: AngebotDto }> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.projekt.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Projekt konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotStornieren(args: {
	id: string;
	angebotId: string;
	grund: string;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.stornieren.post({ grund: args.grund });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Angebot konnte nicht storniert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Legt die neue Fassung an (Entwurf) und liefert sie — das alte Angebot ist danach storniert. */
export async function angebotNeueFassung(args: {
	id: string;
	angebotId: string;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		["neue-fassung"].post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die neue Fassung konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export const angebotArchivQuery = queryOptions({
	queryKey: ["angebote", "archiv"],
	queryFn: async (): Promise<AngebotArchivZeile[]> => {
		const { data, error } = await client.api.angebote.archiv.get();
		if (error !== null)
			throw new Error(`Das Angebotsarchiv lädt nicht (Status ${String(error.status)}).`);
		return data;
	},
});

export const angebotsNummerQuery = queryOptions({
	queryKey: ["angebote", "nummer"],
	queryFn: async (): Promise<NummernStand> => {
		const { data, error } = await client.api["angebots-nummer"].get();
		if (error !== null)
			throw new Error(`Der Nummernstand lädt nicht (Status ${String(error.status)}).`);
		return data;
	},
});

/* ---------- Rechnungen über Lexware Office (12.09.2026, Issue #146) ---------- */

export const rechnungsAutomatikWerte = ["aus", "entwurf", "senden"] as const;
export type RechnungsAutomatik = (typeof rechnungsAutomatikWerte)[number];
export type RechnungsEinstellungDto = {
	automatik: RechnungsAutomatik;
	zahlungszielTage: number;
	steuersatz: number;
	/** Monatliche Betreuung: Olgen legt je Monat die Rechnung an (13.09.2026). */
	monatlichAutomatisch: boolean;
	/** false, solange Nikolas den LEXWARE_API_KEY nicht eingetragen hat. */
	lexwareVerbunden: boolean;
};

export const rechnungsEinstellungQuery = queryOptions({
	queryKey: ["rechnungs-einstellung"],
	queryFn: async (): Promise<RechnungsEinstellungDto> => {
		const { data, error } = await client.api["rechnungs-einstellung"].get();
		if (error !== null) {
			throw new Error(
				`Die Rechnungs-Einstellung konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export async function rechnungsEinstellungSetzen(args: {
	automatik: RechnungsAutomatik;
	zahlungszielTage: number;
	steuersatz: number;
	monatlichAutomatisch: boolean;
}): Promise<RechnungsEinstellungDto> {
	const { data, error } = await client.api["rechnungs-einstellung"].put({
		automatik: args.automatik,
		zahlungszielTage: args.zahlungszielTage,
		steuersatz: args.steuersatz,
		monatlichAutomatisch: args.monatlichAutomatisch,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Einstellung konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Die Rechnung zu einem unterschriebenen Angebot von Hand anstoßen (auch nach einem Fehler). */
export async function angebotRechnungAnlegen(args: {
	id: string;
	angebotId: string;
}): Promise<AngebotDto> {
	const { data, error } = await client.api
		.anfragen({ id: args.id })
		.angebote({ angebotId: args.angebotId })
		.rechnung.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Rechnung konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotsNummerSetzen(args: {
	naechsteLaufend: number;
}): Promise<NummernStand> {
	const { data, error } = await client.api["angebots-nummer"].put(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Nummer konnte nicht umgestellt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export type SchnellangebotDaten = {
	firma: string;
	ansprechpartner: string | null;
	email: string | null;
	telefon: string | null;
	customerId: string | null;
	vorlageId: string;
	variante: string | null;
	notiz: string | null;
	mitKi: boolean;
	adresse?: string | null;
};

/** Der Schnellweg „Angebot schreiben": Interessent + Angebot in einem Zug. */
export async function schnellangebot(
	daten: SchnellangebotDaten,
): Promise<{ interessentId: string; angebotId: string | null }> {
	const { data, error } = await client.api.anfragen.schnellangebot.post(daten);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Angebot konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Das PDF fürs Team — als `<a href download>`, der Cookie reist mit. */
export function angebotPdfUrl(id: string, angebotId: string): string {
	return `/api/anfragen/${encodeURIComponent(id)}/angebote/${encodeURIComponent(angebotId)}/pdf`;
}

export function angebotEreignisseQuery(id: string, angebotId: string) {
	return queryOptions({
		queryKey: ["anfragen", id, "angebote", angebotId, "ereignisse"],
		queryFn: async () => {
			const { data, error } = await client.api
				.anfragen({ id })
				.angebote({ angebotId })
				.ereignisse.get();
			if (error !== null) {
				throw new Error(
					`Der Verlauf konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

/** Der Link, den der Interessent bekommen hat — zum Kopieren in der Akte. */
export function angebotLinkFuer(token: string | null): string | null {
	return token === null ? null : `${window.location.origin}/angebot/${encodeURIComponent(token)}`;
}

// ── Die Kundenseite (offen, der Token ist der Zugang) ──────────────────────

export function angebotOeffentlichQuery(token: string) {
	return queryOptions({
		queryKey: ["angebot", token],
		queryFn: async () => {
			const { data, error } = await client.api.angebot({ token }).get();
			if (error !== null) {
				throw new Error(
					error.status === 404
						? "Dieses Angebot gibt es nicht (mehr)."
						: `Das Angebot konnte nicht geladen werden (Status ${String(error.status)}).`,
				);
			}
			return data;
		},
	});
}

export async function angebotUnterschreiben(args: {
	token: string;
	name: string;
	bild: string;
}): Promise<AngebotOeffentlich> {
	const { data, error } = await client.api
		.angebot({ token: args.token })
		.unterschreiben.post({ name: args.name, bild: args.bild, agbAkzeptiert: true });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Unterschrift konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function angebotAblehnen(args: {
	token: string;
	grund: string | null;
}): Promise<AngebotOeffentlich> {
	const { data, error } = await client.api
		.angebot({ token: args.token })
		.ablehnen.post({ grund: args.grund });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Rückmeldung konnte nicht gespeichert werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export function angebotOeffentlichPdfUrl(token: string): string {
	return `/api/angebot/${encodeURIComponent(token)}/pdf`;
}

// ─── Testimonial-Kontingent (14.09.2026) ────────────────────────────────────

export type { KontingentStand, TestimonialPartnerDto, TestimonialStatus, TestimonialUebersicht };

export const testimonialStatusWerte = [
	"eingetragen",
	"kontaktiert",
	"termin",
	"gedreht",
	"fertig",
	"abgesagt",
] as const;

/** Die Spalten der Tabelle — was der Kunde selbst pflegt. */
export type TestimonialPartnerEingabe = {
	ansprechpartner: string;
	unternehmen: string;
	telefon?: string | null;
	email?: string | null;
	adresse?: string | null;
	plz?: string | null;
	ort?: string | null;
	webseite?: string | null;
	notizen?: string | null;
};

/** Das Team setzt zusätzlich Stand, Termin und Kontingent. */
export type TestimonialPartnerTeamEingabe = TestimonialPartnerEingabe & {
	status?: TestimonialStatus;
	terminAm?: string | null;
	terminNotiz?: string | null;
	kontingentId?: string | null;
};

export const kundenTestimonialsQuery = (customerId: string) =>
	queryOptions({
		queryKey: ["testimonials", customerId],
		queryFn: async (): Promise<TestimonialUebersicht> => {
			const { data, error } = await client.api.kunden({ customerId }).testimonials.get();
			if (error !== null) {
				throw new Error(`Die Testimonial-Liste lädt nicht (Status ${String(error.status)}).`);
			}
			return data;
		},
	});

/** Die eigene Liste im Kundenbereich — leer, solange kein Kontingent und kein Partner existiert. */
export const meineTestimonialsQuery = queryOptions({
	queryKey: ["mein", "testimonials"],
	queryFn: async (): Promise<TestimonialUebersicht> => {
		const { data, error } = await client.api.mein.testimonials.get();
		if (error !== null) {
			throw new Error(`Eure Kundenstimmen laden nicht (Status ${String(error.status)}).`);
		}
		return data;
	},
});

function testimonialFehler(error: { status: number; value: unknown }, fallback: string): Error {
	const wert = error.value;
	const value =
		typeof wert === "string" || typeof wert === "number" || typeof wert === "object" ? wert : null;
	return new Error(fehlermeldungVon({ value, fallback }));
}

export async function testimonialAnlegen(args: {
	customerId: string;
	daten: TestimonialPartnerTeamEingabe;
}): Promise<TestimonialPartnerDto> {
	const { data, error } = await client.api
		.kunden({ customerId: args.customerId })
		.testimonials.post(args.daten);
	if (error !== null) throw testimonialFehler(error, "Der Partner konnte nicht angelegt werden.");
	return data;
}

export async function testimonialImportieren(args: {
	customerId: string;
	text: string;
}): Promise<{ angelegt: number; uebersprungen: number }> {
	const { data, error } = await client.api
		.kunden({ customerId: args.customerId })
		.testimonials.import.post({ text: args.text });
	if (error !== null) throw testimonialFehler(error, "Die Tabelle konnte nicht gelesen werden.");
	return data;
}

export async function kontingentAnlegen(args: {
	customerId: string;
	daten: {
		anzahl: number;
		von?: string | null;
		bis?: string | null;
		notiz?: string | null;
		mitReel?: boolean;
		mitFotos?: boolean;
		reelsJeProduktion?: number | null;
		bilderJeProduktion?: number | null;
	};
}): Promise<KontingentStand> {
	const { data, error } = await client.api
		.kunden({ customerId: args.customerId })
		.kontingente.post({ art: "testimonial", ...args.daten });
	if (error !== null)
		throw testimonialFehler(error, "Das Kontingent konnte nicht angelegt werden.");
	return { ...data, belegt: 0, fertig: 0, frei: data.anzahl };
}

export async function kontingentLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.kontingente({ id: args.id }).delete();
	if (error !== null)
		throw testimonialFehler(error, "Das Kontingent konnte nicht gelöscht werden.");
}

export async function testimonialAendern(args: {
	id: string;
	daten: Partial<TestimonialPartnerTeamEingabe>;
}): Promise<TestimonialPartnerDto> {
	const { data, error } = await client.api.testimonials({ id: args.id }).patch(args.daten);
	if (error !== null)
		throw testimonialFehler(error, "Die Änderung konnte nicht gespeichert werden.");
	return data;
}

export async function testimonialLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.testimonials({ id: args.id }).delete();
	if (error !== null) throw testimonialFehler(error, "Der Partner konnte nicht gelöscht werden.");
}

export async function testimonialProduktionStarten(args: {
	id: string;
}): Promise<{ projektId: string; partner: TestimonialPartnerDto }> {
	const { data, error } = await client.api.testimonials({ id: args.id }).produktion.post();
	if (error !== null)
		throw testimonialFehler(error, "Die Produktion konnte nicht gestartet werden.");
	return data;
}

export async function meinTestimonialAnlegen(
	daten: TestimonialPartnerEingabe,
): Promise<TestimonialPartnerDto> {
	const { data, error } = await client.api.mein.testimonials.post(daten);
	if (error !== null)
		throw testimonialFehler(error, "Der Partner konnte nicht eingetragen werden.");
	return data;
}

export async function meinTestimonialAendern(args: {
	id: string;
	daten: Partial<TestimonialPartnerEingabe>;
}): Promise<TestimonialPartnerDto> {
	const { data, error } = await client.api.mein.testimonials({ id: args.id }).patch(args.daten);
	if (error !== null)
		throw testimonialFehler(error, "Die Änderung konnte nicht gespeichert werden.");
	return data;
}

// ─── Siri-Kurzbefehl (15.09.2026): der persönliche Token im Profil ─────────

export type { ReminderDto };

/** Hat dieses Konto einen Siri-Token? Der Klartext ist nie abrufbar — nur „aktiv" ja/nein. */
export const kontoSiriQuery = queryOptions({
	queryKey: ["konto", "siri"],
	queryFn: async (): Promise<{ aktiv: boolean }> => {
		const { data, error } = await client.api.konto.siri.get();
		if (error !== null) {
			throw new Error(
				`Der Siri-Stand konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

/** Neuer Token — ein alter erlischt sofort. Der Klartext kommt genau einmal zurück. */
export async function siriTokenErzeugen(): Promise<{ token: string }> {
	const { data, error } = await client.api.konto.siri.token.post();
	if (error !== null) {
		throw new Error(`Der Token konnte nicht erzeugt werden (Status ${String(error.status)}).`);
	}
	return data;
}

export async function siriTokenWiderrufen(): Promise<void> {
	const { error } = await client.api.konto.siri.token.delete();
	if (error !== null) {
		throw new Error(`Der Token konnte nicht widerrufen werden (Status ${String(error.status)}).`);
	}
}

/** Die Adresse, die in den Kurzbefehl gehört — immer https, sonst antwortet der Server leer. */
export function siriEndpunktUrl(): string {
	return `${window.location.origin}/api/reminders`;
}

// ─── Angebotsrechner: Katalog, Kalkulationen, Stammdaten (21.09.2026) ────────

export type {
	Abrechnung,
	Ampel,
	BausteinDaten,
	BausteinKategorie,
	BausteinRolle,
	Betreuung,
	BetreuungPhase,
	BetreuungsStufe,
	DistanzErgebnis,
	Drehtag,
	DrehtagTyp,
	DrehtagTypRegel,
	EngineErgebnis,
	FixkostenDaten,
	FixkostenDto,
	KalkBausteinDto,
	KalkParameter,
	KalkulationDaten,
	KalkulationDto,
	KalkulationStatus,
	KalkulationZeile,
	KalkVorlageDaten,
	KalkVorlageDto,
	Katalog,
	KostensatzErgebnis,
	Kundentyp,
	Land,
	PaketZiel,
	PersonDaten,
	PersonDto,
	PersonRolle,
	Position,
	PositionTyp,
	PruefStufe,
	Pruefung,
	ReiseErgebnis,
	Rhythmus,
	Stammdaten,
	Stufe,
	StufenErgebnis,
	Szenario,
	ZaehltAls,
	Zahlungsbedingung,
	Zone,
};

/** Ein 422 beim Speichern trägt die offenen Prüfpunkte mit — die Oberfläche zeigt sie. */
export class KalkulationSpeicherFehler extends Error {
	constructor(
		message: string,
		readonly pruefungen: Pruefung[],
	) {
		super(message);
	}
}

function kalkFehler(args: { value: object | string | number | null; fallback: string }): Error {
	const text = fehlermeldungVon(args);
	if (
		typeof args.value === "object" &&
		args.value !== null &&
		"pruefungen" in args.value &&
		Array.isArray(args.value.pruefungen)
	) {
		return new KalkulationSpeicherFehler(text, args.value.pruefungen as Pruefung[]);
	}
	return new Error(text);
}

export const kalkKatalogQuery = queryOptions({
	queryKey: ["kalkulation", "katalog"],
	queryFn: async (): Promise<Katalog> => {
		const { data, error } = await client.api.kalkulation.katalog.get();
		if (error !== null) {
			throw new Error(
				`Der Baustein-Katalog konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
	staleTime: 5 * 60 * 1000,
});

export const kalkKapazitaetQuery = queryOptions({
	queryKey: ["kalkulation", "kapazitaet"],
	queryFn: async () => {
		const { data, error } = await client.api.kalkulation.kapazitaet.get();
		if (error !== null) {
			throw new Error(
				`Die Kapazität konnte nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export const kalkulationenQuery = queryOptions({
	queryKey: ["kalkulationen"],
	queryFn: async (): Promise<KalkulationZeile[]> => {
		const { data, error } = await client.api.kalkulationen.get();
		if (error !== null) {
			throw new Error(
				`Die Kalkulationen konnten nicht geladen werden (Status ${String(error.status)}).`,
			);
		}
		return data;
	},
});

export function kalkulationQuery(id: string) {
	return queryOptions({
		queryKey: ["kalkulationen", id],
		queryFn: async (): Promise<KalkulationDto> => {
			const { data, error } = await client.api.kalkulationen({ id }).get();
			if (error !== null) {
				throw new Error(
					fehlermeldungVon({
						value: error.value,
						fallback: `Die Kalkulation konnte nicht geladen werden (Status ${String(error.status)}).`,
					}),
				);
			}
			return data;
		},
	});
}

export async function kalkulationBerechnen(args: {
	daten: KalkulationDaten;
	kalkulationId: string | null;
}): Promise<EngineErgebnis> {
	const { data, error } = await client.api.kalkulation.berechnen.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Kalkulation konnte nicht berechnet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function kalkulationSpeichern(args: {
	id: string | null;
	daten: KalkulationDaten;
}): Promise<KalkulationDto> {
	const { data, error } =
		args.id === null
			? await client.api.kalkulationen.post(args.daten)
			: await client.api.kalkulationen({ id: args.id }).put(args.daten);
	if (error !== null) {
		throw kalkFehler({
			value: error.value,
			fallback: `Die Kalkulation konnte nicht gespeichert werden (Status ${String(error.status)}).`,
		});
	}
	return data;
}

export async function kalkulationFreigeben(args: {
	id: string;
	daten: KalkulationDaten;
}): Promise<KalkulationDto> {
	const { data, error } = await client.api
		.kalkulationen({ id: args.id })
		.freigeben.post(args.daten);
	if (error !== null) {
		throw kalkFehler({
			value: error.value,
			fallback: `Die Freigabe ist fehlgeschlagen (Status ${String(error.status)}).`,
		});
	}
	return data;
}

export async function kalkulationStatusSetzen(args: {
	id: string;
	status: KalkulationStatus;
}): Promise<KalkulationDto> {
	const { data, error } = await client.api
		.kalkulationen({ id: args.id })
		.status.patch({ status: args.status });
	if (error !== null) {
		throw kalkFehler({
			value: error.value,
			fallback: `Der Status konnte nicht gesetzt werden (Status ${String(error.status)}).`,
		});
	}
	return data;
}

/** Phase 4: Aus der Kalkulation entsteht ein Angebot im Angebotstool (Entwurf). */
export async function kalkulationAngebotErzeugen(args: {
	id: string;
}): Promise<{ angebotId: string; interessentId: string }> {
	const { data, error } = await client.api.kalkulationen({ id: args.id }).angebot.post();
	if (error !== null) {
		throw kalkFehler({
			value: error.value,
			fallback: `Das Angebot konnte nicht erzeugt werden (Status ${String(error.status)}).`,
		});
	}
	return data;
}

export async function kalkulationLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.kalkulationen({ id: args.id }).delete();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Kalkulation konnte nicht gelöscht werden (Status ${String(error.status)}).`,
			}),
		);
	}
}

export async function kalkVorlageAnwenden(args: { vorlageId: string }): Promise<{
	positionen: Position[];
	drehtage: Drehtag[];
	betreuung: Betreuung | null;
	fehlend: string[];
}> {
	const { data, error } = await client.api.kalkulation
		.vorlagen({ id: args.vorlageId })
		.anwenden.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Vorlage konnte nicht geladen werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Ein Paket zur laufenden Kalkulation hinzufügen — eigener, gleicher oder Betreuungs-Drehtag (23.09.2026). */
export async function kalkPaketHinzufuegen(args: {
	positionen: Position[];
	drehtage: Drehtag[];
	vorlageId: string;
	ziel: PaketZiel;
}): Promise<{
	positionen: Position[];
	drehtage: Drehtag[];
	betreuung: Betreuung | null;
	fehlend: string[];
	hinweise: string[];
}> {
	const { data, error } = await client.api.kalkulation.paket.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Das Paket konnte nicht hinzugefügt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Betreuung einsetzen, neu erzeugen oder herausnehmen — das Einzelprojekt bleibt stehen. */
export async function kalkBetreuungEinsetzen(args: {
	positionen: Position[];
	drehtage: Drehtag[];
	betreuung: Betreuung | null;
}): Promise<{ positionen: Position[]; drehtage: Drehtag[]; fehlend: string[] }> {
	const { data, error } = await client.api.kalkulation.betreuung.einsetzen.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Betreuung konnte nicht berechnet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Weitere Variante: Kopie in derselben Gruppe — jede Variante wird ein eigenes Angebot. */
/** Korrektur per Text/Sprache: Olgen baut die Kalkulation um, auf Wunsch alle Varianten der Gruppe. */
export async function kalkUeberarbeiten(args: {
	id: string;
	daten: KalkulationDaten;
	korrektur: string;
	alleVarianten: boolean;
}): Promise<{ daten: KalkulationDaten; geaendert: string; weitereVarianten: number }> {
	const { data, error } = await client.api.kalkulationen({ id: args.id }).ueberarbeiten.post({
		daten: args.daten,
		korrektur: args.korrektur,
		alleVarianten: args.alleVarianten,
	});
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Korrektur konnte nicht umgesetzt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function kalkVarianteAnlegen(args: { id: string }): Promise<KalkulationDto> {
	const { data, error } = await client.api.kalkulationen({ id: args.id }).variante.post();
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Variante konnte nicht angelegt werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

/** Anfrage einlesen: E-Mail-Text und/oder Dateien → je Variante eine Kalkulation (KI). */
export async function kalkAnfrageLesen(args: {
	text: string | null;
	dateien: File[];
	interessentId: string | null;
	customerId: string | null;
	adresse: string | null;
}): Promise<{ kalkulationIds: string[]; zusammenfassung: string; rueckfragen: string[] }> {
	const dateien = await Promise.all(
		args.dateien.map(async (d) => ({ name: d.name, base64: await dateiAlsBase64(d) })),
	);
	const { data, error } = await client.api.kalkulation.anfrage.post({ ...args, dateien });
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Anfrage konnte nicht gelesen werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function kalkPositionAusBaustein(args: {
	bausteinId: string;
	menge: number;
}): Promise<Position> {
	const { data, error } = await client.api.kalkulation.position.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Der Baustein konnte nicht übernommen werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

export async function kalkDistanz(args: {
	adresse: string;
}): Promise<DistanzErgebnis & { quelle?: "cache" | "api" }> {
	const { data, error } = await client.api.kalkulation.distanz.post(args);
	if (error !== null) {
		throw new Error(
			fehlermeldungVon({
				value: error.value,
				fallback: `Die Entfernung konnte nicht berechnet werden (Status ${String(error.status)}).`,
			}),
		);
	}
	return data;
}

// --- Stammdaten (Inhaber) ------------------------------------------------------

export const kalkStammdatenQuery = queryOptions({
	queryKey: ["kalkulation", "stammdaten"],
	queryFn: async (): Promise<Stammdaten> => {
		const { data, error } = await client.api.kalkulation.stammdaten.get();
		if (error !== null) {
			throw new Error(
				fehlermeldungVon({
					value: error.value,
					fallback: `Die Stammdaten konnten nicht geladen werden (Status ${String(error.status)}).`,
				}),
			);
		}
		return data;
	},
});

function stammdatenFehler(
	status: number,
	value: object | string | number | null,
	was: string,
): Error {
	return new Error(
		fehlermeldungVon({ value, fallback: `${was} ist fehlgeschlagen (Status ${String(status)}).` }),
	);
}

export async function kalkPersonSpeichern(args: {
	id: string | null;
	daten: PersonDaten;
}): Promise<PersonDto> {
	const { data, error } = await client.api.kalkulation.personen.put(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Speichern der Person");
	return data;
}
export async function kalkPersonLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.kalkulation.personen({ id: args.id }).delete();
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Löschen der Person");
}
export async function kalkPersonenZuruecksetzen(): Promise<PersonDto[]> {
	const { data, error } = await client.api.kalkulation.personen.zuruecksetzen.post();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Zurücksetzen der Personen");
	return data;
}

export async function kalkFixkostenSpeichern(args: {
	id: string | null;
	daten: FixkostenDaten;
}): Promise<FixkostenDto> {
	const { data, error } = await client.api.kalkulation.fixkosten.put(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Speichern der Fixkosten");
	return data;
}
export async function kalkFixkostenLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.kalkulation.fixkosten({ id: args.id }).delete();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Löschen der Fixkosten-Zeile");
}
export async function kalkFixkostenZuruecksetzen(): Promise<FixkostenDto[]> {
	const { data, error } = await client.api.kalkulation.fixkosten.zuruecksetzen.post();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Zurücksetzen der Fixkosten");
	return data;
}

export async function kalkParameterSetzen(
	parameter: Partial<KalkParameter>,
): Promise<KalkParameter> {
	const { data, error } = await client.api.kalkulation.parameter.put(parameter);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Speichern der Parameter");
	return data;
}
export async function kalkParameterZuruecksetzen(): Promise<KalkParameter> {
	const { data, error } = await client.api.kalkulation.parameter.zuruecksetzen.post();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Zurücksetzen der Parameter");
	return data;
}

export async function kalkBausteinSpeichern(args: {
	id: string | null;
	daten: BausteinDaten;
}): Promise<KalkBausteinDto> {
	const { data, error } = await client.api.kalkulation.bausteine.put(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Speichern des Bausteins");
	return data;
}
export async function kalkBausteinLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.kalkulation.bausteine({ id: args.id }).delete();
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Löschen des Bausteins");
}
export async function kalkBausteineZuruecksetzen(): Promise<KalkBausteinDto[]> {
	const { data, error } = await client.api.kalkulation.bausteine.zuruecksetzen.post();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Zurücksetzen der Bausteine");
	return data;
}
export async function kalkBausteinAusPosition(args: {
	position: Position;
	kategorie: BausteinKategorie;
}): Promise<KalkBausteinDto> {
	const { data, error } = await client.api.kalkulation.bausteine["aus-position"].post(args);
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Übernehmen in den Katalog");
	return data;
}

export async function kalkVorlageSpeichern(args: {
	id: string | null;
	daten: KalkVorlageDaten;
}): Promise<KalkVorlageDto> {
	const { data, error } = await client.api.kalkulation.vorlagen.put(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Speichern der Vorlage");
	return data;
}
export async function kalkVorlageLoeschen(args: { id: string }): Promise<void> {
	const { error } = await client.api.kalkulation.vorlagen({ id: args.id }).delete();
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Löschen der Vorlage");
}
export async function kalkVorlagenZuruecksetzen(): Promise<KalkVorlageDto[]> {
	const { data, error } = await client.api.kalkulation.vorlagen.zuruecksetzen.post();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Zurücksetzen der Vorlagen");
	return data;
}

// ─── Angebotsrechner Phase 5: Zahlen & Kapazität (22.09.2026) ────────────────

export type {
	AenderungDto,
	EinstellungEingabe,
	EinstellungErgebnis,
	FinanzenUebersicht,
	FinanzVorschlagArt,
	FinanzVorschlagStatus,
	MonatDaten,
	MonatDto,
	VorschlagDto,
};

export const kalkFinanzenQuery = queryOptions({
	queryKey: ["kalkulation", "finanzen"],
	queryFn: async (): Promise<FinanzenUebersicht> => {
		const { data, error } = await client.api.kalkulation.finanzen.get();
		if (error !== null) {
			throw new Error(
				fehlermeldungVon({
					value: error.value,
					fallback: `Die Zahlen konnten nicht geladen werden (Status ${String(error.status)}).`,
				}),
			);
		}
		return data;
	},
});

export async function kalkMonatSpeichern(args: {
	monat: string;
	daten: MonatDaten;
}): Promise<MonatDto> {
	const { data, error } = await client.api.kalkulation.finanzen
		.monate({ monat: args.monat })
		.put(args.daten);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Speichern des Monats");
	return data;
}

export async function kalkEinstellungRechnen(
	eingabe: EinstellungEingabe,
): Promise<EinstellungErgebnis> {
	const { data, error } = await client.api.kalkulation.finanzen.einstellung.post(eingabe);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Rechnen der Einstellung");
	return data;
}

export type KalkUpload = { name: string; base64: string | null; text: string | null };

export async function kalkBwaHochladen(args: {
	monat: string;
	dateien: KalkUpload[];
}): Promise<{ vorschlaege: VorschlagDto[] }> {
	const { data, error } = await client.api.kalkulation.finanzen.bwa.post(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Lesen der BWA");
	return data;
}

export async function kalkKontoauszugHochladen(args: {
	datei: KalkUpload;
}): Promise<{ monate: number; vorschlaege: VorschlagDto[] }> {
	const { data, error } = await client.api.kalkulation.finanzen.kontoauszug.post(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Lesen des Kontoauszugs");
	return data;
}

export async function kalkBelegeHochladen(args: {
	dateien: KalkUpload[];
}): Promise<{ vorschlaege: VorschlagDto[] }> {
	const { data, error } = await client.api.kalkulation.finanzen.belege.post(args);
	if (error !== null) throw stammdatenFehler(error.status, error.value, "Lesen der Belege");
	return data;
}

export async function kalkVorschlagEntscheiden(args: {
	id: string;
	aktion: "bestaetigen" | "verwerfen";
}): Promise<VorschlagDto> {
	const { data, error } = await client.api.kalkulation.finanzen
		.vorschlaege({ id: args.id })({ aktion: args.aktion })
		.post();
	if (error !== null)
		throw stammdatenFehler(error.status, error.value, "Entscheiden des Vorschlags");
	return data;
}
